import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Check, Calendar, Users, Sparkles, Zap, Clock, DollarSign, ArrowRight, Package, Activity, Shield, FileText, Code, Key, Fingerprint, Lock, TrendingUp, Star, Link2, QrCode, Camera, Bell, Download, Mail, Menu, X, Globe, Bitcoin, BadgeDollarSign, Percent, SlidersHorizontal, BarChart3, Anchor, MapPin, Database, ArrowLeftRight, Megaphone, MousePointerClick, Compass, MessageCircle, LifeBuoy, CloudSun, Waves, Wind, Moon, RefreshCw, FileSpreadsheet, Handshake } from 'lucide-react';
import { BeaconIcon } from '../components/BeaconIcon';
import { QRCodeSVG as QRCodeComponent } from 'qrcode.react';
import { getAllPlans, formatPrice } from '../config/pricing';
import { SocialLinksDark } from '../components/SocialLinks';
import HelpScoutBeacon from '../components/HelpScoutBeacon';
import { KioskDirectSection } from '../components/KioskDirectSection';
import { proxyImg } from '../utils/img';

// Auto-rotating top announcement banner slides (module scope = stable identity).
const ANNOUNCEMENTS = [
  {
    href: '/customers/boatx',
    bg: 'bg-gradient-to-r from-blue-600 via-blue-500 to-indigo-600 hover:from-blue-700 hover:via-blue-600 hover:to-indigo-700',
    text: 'text-white',
    bold: 'BOATX processed $100k+ in bookings',
    rest: '\u00A0through BookingFlows',
    cta: '— see how',
  },
  {
    href: '/customers/banana-boat-rentals',
    bg: 'bg-gradient-to-r from-blue-600 via-blue-500 to-indigo-600 hover:from-blue-700 hover:via-blue-600 hover:to-indigo-700',
    text: 'text-white',
    bold: 'Welcoming Banana Boat Rentals',
    rest: '\u00A0— 15+ boat fleet now managed on BookingFlows',
    cta: 'read their story',
  },
  {
    href: '/meet-the-team',
    bg: 'bg-gradient-to-r from-blue-600 via-blue-500 to-indigo-600 hover:from-blue-700 hover:via-blue-600 hover:to-indigo-700',
    text: 'text-white',
    bold: 'USCG · RYA · MCA · IYT · ASA',
    rest: '\u00A0— built by certified captains',
    cta: 'meet the team',
  },
];

// One full set of "Powered by best-in-class infrastructure" logos.
// Rendered twice inside the marquee for a seamless infinite loop.
const InfraLogoSet = ({ ariaHidden }) => (
  <div className="flex items-center gap-x-12 pr-12 shrink-0" aria-hidden={ariaHidden || undefined}>
    {/* Google */}
    <div className="flex items-center gap-2 shrink-0" title="Google Business Profile">
      <img src="/images/integrations/google-g.png" alt="Google" className="h-6 w-6" loading="lazy" />
      <span className="text-sm font-medium text-gray-600 whitespace-nowrap">Google</span>
    </div>
    {/* Stripe */}
    <div className="flex items-center shrink-0" title="Stripe — payments">
      <svg className="w-12 h-5" viewBox="0 0 60 25" fill="none" aria-hidden="true">
        <path fillRule="evenodd" clipRule="evenodd" d="M60 12.8c0-4.28-2.07-7.66-6.03-7.66-3.98 0-6.38 3.38-6.38 7.63 0 5.04 2.84 7.58 6.92 7.58 1.99 0 3.49-.45 4.63-1.09v-3.33c-1.14.57-2.45.93-4.11.93-1.63 0-3.07-.57-3.25-2.56h8.19c0-.22.03-1.08.03-1.5zm-8.19-1.59c0-1.88 1.15-2.67 2.19-2.67 1.02 0 2.1.79 2.1 2.67h-4.29zM41.3 5.14c-1.64 0-2.7.77-3.28 1.3l-.22-1.04H34v19.8l4.22-.9v-4.8c.6.44 1.49 1.06 2.97 1.06 3 0 5.74-2.42 5.74-7.77 0-4.9-2.78-7.65-5.63-7.65zm-.99 11.76c-.99 0-1.57-.36-1.98-.8v-6.3c.44-.48 1.04-.82 1.98-.82 1.52 0 2.57 1.7 2.57 3.94 0 2.3-1.03 3.98-2.57 3.98zM27.65 4.3l4.24-.9V0l-4.24.9v3.4zm0 .98h4.24v14.6h-4.24V5.28zM22.94 6.6l-.26-1.32h-3.63v14.6h4.22v-9.9c1-.3 2.68-1.06 3.2-.88V5.28c-.54-.2-2.51-.57-3.53 1.32zM13.68 1.38L9.56 2.25v13.35c0 2.47 1.85 4.28 4.3 4.28 1.36 0 2.35-.25 2.9-.55v-3.45c-.53.22-3.17 1-3.17-1.5V8.93h3.17V5.28h-3.17l.09-3.9zM4.16 9.84c0-.65.54-.9 1.42-.9 1.27 0 2.87.39 4.14 1.08V6.2c-1.39-.55-2.76-.77-4.14-.77C2.25 5.43 0 7.1 0 10.02c0 4.5 6.2 3.78 6.2 5.72 0 .77-.67 1.02-1.6 1.02-1.39 0-3.17-.57-4.58-1.35v3.82c1.56.67 3.14.96 4.58.96 3.35 0 5.58-1.66 5.58-4.72 0-4.86-6.22-4-6.22-5.63z" fill="#635BFF"/>
      </svg>
    </div>
    {/* QuickBooks — LIVE (Intuit production keys, official integration) */}
    <div className="flex items-center shrink-0" title="QuickBooks Online — automatic revenue &amp; expense sync">
      <img src="/images/integrations/quickbooks-logo.png" alt="Intuit QuickBooks" className="h-6 w-auto" loading="lazy" data-testid={ariaHidden ? undefined : 'trusted-infra-quickbooks'} />
    </div>
    {/* Render */}
    <div className="flex items-center shrink-0" title="Render — hosted infrastructure">
      <img src="/images/integrations/render-icon.png" alt="Render" className="h-7 w-auto" loading="lazy" />
    </div>
    {/* AWS */}
    <div className="flex items-center shrink-0" title="Amazon Web Services — encrypted daily data backups">
      <img src="/images/integrations/aws-logo.svg" alt="AWS" className="h-5 w-auto" loading="lazy" data-testid="trusted-infra-aws" />
    </div>
    {/* Cloudflare */}
    <div className="flex items-center shrink-0" title="Cloudflare — security & global CDN">
      <img src="/images/integrations/cloudflare-logo.png" alt="Cloudflare" className="h-[18px] w-auto" loading="lazy" />
    </div>
    {/* WhatsApp */}
    <div className="flex items-center gap-2 shrink-0" title="WhatsApp — customer messaging">
      <img src="/images/integrations/whatsapp-logo.webp" alt="WhatsApp" className="h-6 w-6 rounded-md" loading="lazy" data-testid="trusted-infra-whatsapp" />
      <span className="text-sm font-medium text-gray-600 whitespace-nowrap">WhatsApp</span>
    </div>
    {/* Twilio */}
    <div className="flex items-center gap-2 shrink-0" title="Twilio — SMS & WhatsApp notifications">
      <img src="/images/integrations/twilio-icon.svg" alt="Twilio" className="h-5 w-5" loading="lazy" data-testid="trusted-infra-twilio" />
      <span className="text-sm font-medium text-gray-600 whitespace-nowrap">Twilio</span>
    </div>
    {/* Zapier */}
    <div className="flex items-center shrink-0" title="Zapier — booking events into 8,000+ apps">
      <svg className="h-5 w-5" viewBox="0 0 24 24" fill="#FF4F00" aria-hidden="true" data-testid="trusted-infra-zapier">
        <path d="M14.13 12.01c0 .62-.11 1.22-.32 1.77-.55.21-1.15.33-1.78.33h-.01c-.63 0-1.23-.12-1.78-.33-.21-.55-.33-1.15-.33-1.77v-.01c0-.63.12-1.23.33-1.78.55-.21 1.15-.33 1.78-.33h.01c.63 0 1.23.12 1.78.33.21.55.32 1.15.32 1.78v.01zm7.6-1.35h-5.71l4.04-4.04a10.6 10.6 0 0 0-1.07-1.26 10.6 10.6 0 0 0-1.26-1.07l-4.04 4.04V2.62c-.54-.09-1.1-.14-1.66-.14h-.01c-.57 0-1.12.05-1.67.14v5.71L6.31 4.29c-.45.32-.87.68-1.26 1.07-.39.39-.75.81-1.07 1.26l4.04 4.04H2.31s-.14 1.1-.14 1.67v.01c0 .57.05 1.12.14 1.66h5.71l-4.04 4.04c.64.9 1.43 1.69 2.33 2.33l4.04-4.04v5.71c.54.09 1.1.14 1.66.14h.02c.56 0 1.12-.05 1.66-.14v-5.71l4.04 4.04c.45-.32.87-.68 1.26-1.07.39-.39.75-.81 1.07-1.26l-4.04-4.04h5.71c.09-.54.14-1.1.14-1.66v-.02c0-.56-.05-1.12-.14-1.66z" />
      </svg>
    </div>
    {/* Help Scout */}
    <div className="flex items-center shrink-0" title="Help Scout — support desk &amp; AI answers">
      <img src="https://customer-assets-0z36b82j.emergentagent.net/job_f27fe237-052e-4979-a423-fd4be621e1f9/artifacts/ynhf9qa7_android-chrome-512x512.png" alt="Help Scout" className="h-6 w-6" loading="lazy" data-testid="trusted-infra-helpscout" />
    </div>
    {/* Stormglass */}
    <div className="flex items-center shrink-0" title="Stormglass — marine weather, tide &amp; astronomy data">
      <img src="/images/integrations/stormglass.png" alt="Stormglass" className="h-6 w-6 rounded-md" loading="lazy" data-testid="trusted-infra-stormglass" />
    </div>
    {/* Grafana */}
    <div className="flex items-center gap-2 shrink-0" title="Grafana — 24/7 performance monitoring &amp; alerting">
      <img src="/images/integrations/grafana.svg" alt="Grafana" className="h-5 w-5" loading="lazy" data-testid="trusted-infra-grafana" />
      <span className="text-sm font-medium text-gray-600 whitespace-nowrap">Grafana</span>
    </div>
    {/* Sentry */}
    <div className="flex items-center gap-2 shrink-0" title="Sentry — real-time error monitoring">
      <img src="/images/integrations/sentry.svg" alt="Sentry" className="h-5 w-5" loading="lazy" data-testid="trusted-infra-sentry" />
      <span className="text-sm font-medium text-gray-600 whitespace-nowrap">Sentry</span>
    </div>
    {/* Semgrep */}
    <div className="flex items-center gap-2 shrink-0" title="Semgrep — continuous code security scanning">
      <img src="/images/integrations/semgrep.png" alt="Semgrep" className="h-5 w-5 rounded" loading="lazy" data-testid="trusted-infra-semgrep" />
      <span className="text-sm font-medium text-gray-600 whitespace-nowrap">Semgrep</span>
    </div>
  </div>
);


const SupplierLandingPage = () => {
  const navigate = useNavigate();
  const plans = getAllPlans(false); // Use demo pricing
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  // Operator spotlight carousel — FBWS (international) first, then BBR
  const [spotSlide, setSpotSlide] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setSpotSlide((s) => (s + 1) % 3), 9000);
    return () => clearInterval(t);
  }, []);
  // Top announcement banner — slides up after the user scrolls past the hero
  const [bannerVisible, setBannerVisible] = useState(true);
  // Floating "Start free" CTA — only appears once the user is past the hero,
  // so it doesn't compete with the in-hero buttons. Pure conversion nudge for
  // visitors who scroll deep without ever returning to the nav.
  const [showFloatingCta, setShowFloatingCta] = useState(false);

  // Typed rotator for the gradient hero buzz-line. Cycles through a small set
  // of operator-resonant outcomes (Less busywork → More time for what matters
  // → Happier customers → …). Pure CSS caret + JS typing/erasing — no deps.
  // Honors prefers-reduced-motion: users who opt out see the first phrase
  // rendered statically with no animation.
  const BUZZ_LINES = [
    'Less busywork.',
    'More time.',
    'Happy customers.',
    'Higher margins.',
    'Fewer no-shows.',
  ];
  const [buzzIdx, setBuzzIdx] = useState(0);
  const [buzzText, setBuzzText] = useState(BUZZ_LINES[0]);
  const [buzzPhase, setBuzzPhase] = useState('hold'); // hold → erase → type → hold
  useEffect(() => {
    const prefersReduced = typeof window !== 'undefined' &&
      window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReduced) return; // Leave the static first phrase visible.

    const fullText = BUZZ_LINES[buzzIdx];
    let timer;
    if (buzzPhase === 'hold') {
      timer = setTimeout(() => setBuzzPhase('erase'), 1800);
    } else if (buzzPhase === 'erase') {
      if (buzzText.length === 0) {
        const next = (buzzIdx + 1) % BUZZ_LINES.length;
        setBuzzIdx(next);
        setBuzzPhase('type');
      } else {
        timer = setTimeout(() => setBuzzText(buzzText.slice(0, -1)), 35);
      }
    } else if (buzzPhase === 'type') {
      if (buzzText.length === fullText.length) {
        setBuzzPhase('hold');
      } else {
        timer = setTimeout(() => setBuzzText(fullText.slice(0, buzzText.length + 1)), 65);
      }
    }
    return () => clearTimeout(timer);
  }, [buzzPhase, buzzText, buzzIdx]);

  // Auto-rotating announcement banner index (crossfade). Pauses on hover;
  // honors prefers-reduced-motion (no auto-advance).
  const [announceIdx, setAnnounceIdx] = useState(0);
  const [announcePaused, setAnnouncePaused] = useState(false);
  useEffect(() => {
    const prefersReduced = typeof window !== 'undefined' &&
      window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReduced || announcePaused) return;
    const id = setInterval(() => {
      setAnnounceIdx((i) => (i + 1) % ANNOUNCEMENTS.length);
    }, 6000);
    return () => clearInterval(id);
  }, [announcePaused]);

  // Lightweight wrapper around analytics util (kept inside component so the
  // import isn't loaded on first paint).
  const trackSignupStarted = (source) => {
    import('../utils/analytics').then((m) => {
      if (typeof m.trackSignupStarted === 'function') {
        m.trackSignupStarted(source);
      }
    }).catch(() => {});
  };

  // Add scroll animation observer + banner auto-hide on scroll
  useEffect(() => {
    // Fire supplier page view — B2B lead signal for Google Ads
    import('../utils/analytics').then(({ trackSupplierPageView }) => {
      trackSupplierPageView('/for-suppliers');
    });

    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -100px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-fade-in-up');
        }
      });
    }, observerOptions);

    // Observe all sections with the class 'animate-on-scroll'
    document.querySelectorAll('.animate-on-scroll').forEach((el) => {
      observer.observe(el);
    });

    // Hide top banner once user has scrolled past ~120px (past hero start).
    // Show the floating "Start free" CTA once the user is past ~700px (deep
    // enough that they've engaged with the page but no longer see the
    // primary hero CTAs).
    const onScroll = () => {
      const y = window.scrollY;
      setBannerVisible(y < 120);
      setShowFloatingCta(y > 700);
    };
    window.addEventListener('scroll', onScroll, { passive: true });

    return () => {
      observer.disconnect();
      window.removeEventListener('scroll', onScroll);
    };
  }, []);

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <HelpScoutBeacon />
      <Helmet>
        <title>Online Booking Software for Tours, Charters &amp; Rentals | BookingFlows</title>
        <meta name="description" content="BookingFlows is online booking software for tours, charters, rentals &amp; experience businesses. Take bookings 24/7, get paid automatically, manage your team. Sign up free." />
        <meta property="og:title" content="Online Booking Software | BookingFlows" />
        <meta property="og:description" content="Take bookings 24/7, get paid automatically, manage your team. Free to start." />
        {/* Canonical points to the root — / and /for-suppliers serve the same
            page so this avoids duplicate-content penalties while keeping the
            /for-suppliers route alive for existing Google Ads campaigns. */}
        <link rel="canonical" href="https://bookingflows.com/" />
      </Helmet>
      <style>{`
        /* Smooth scroll animations - subtle and professional */
        .animate-on-scroll {
          opacity: 0;
          transform: translateY(16px);
          transition: opacity 0.6s ease-out, transform 0.6s ease-out;
        }
        
        .animate-fade-in-up {
          opacity: 1;
          transform: translateY(0);
        }

        /* Smooth scroll behavior */
        html {
          scroll-behavior: smooth;
        }
        
        /* Enhanced button hover states */
        .btn-supplier-primary {
          transition: all 0.2s ease;
        }
        .btn-supplier-primary:hover {
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(37, 99, 235, 0.25);
        }
        .btn-supplier-primary:active {
          transform: translateY(0);
        }
      `}</style>
      
      {/* Top announcement banner — auto-rotating crossfade (BOATX blue ⇄ Banana
          Boat Rentals yellow ⇄ credential ribbon). Fixed height (h-11) so every
          slide stays vertically centered on all screens. Pauses on hover;
          auto-hides on scroll past the hero. */}
      <div
        data-testid="supplier-top-banner"
        className={`relative overflow-hidden transition-[max-height,opacity] duration-300 ease-out ${
          bannerVisible ? 'h-11 max-h-12 opacity-100' : 'h-11 max-h-0 opacity-0'
        }`}
        onMouseEnter={() => setAnnouncePaused(true)}
        onMouseLeave={() => setAnnouncePaused(false)}
      >
        {ANNOUNCEMENTS.map((a, i) => (
          <a
            key={i}
            href={a.href}
            data-testid={`top-banner-slide-${i}`}
            aria-hidden={i !== announceIdx}
            className={`absolute inset-0 flex items-center justify-center text-center text-xs sm:text-sm font-medium tracking-wide leading-none transition-opacity duration-700 ease-out ${a.bg} ${a.text} ${
              i === announceIdx ? 'opacity-100' : 'opacity-0 pointer-events-none'
            }`}
          >
            <div className="w-full max-w-7xl mx-auto px-3 sm:px-4 flex items-center justify-center gap-1.5 sm:gap-2">
              <span>
                <span className="font-semibold">{a.bold}</span>
                <span className="hidden sm:inline">{a.rest}</span>
              </span>
              <span className="opacity-90 underline-offset-2 hover:underline whitespace-nowrap inline-flex items-center gap-1 shrink-0">
                {a.cta}
                <ArrowRight className="w-3.5 h-3.5" />
              </span>
            </div>
          </a>
        ))}
      </div>

      {/* Header/Nav */}
      <header className="border-b border-gray-100 sticky top-0 z-50 bg-white/90 backdrop-blur-md shadow-sm shadow-gray-200/40 pt-[env(safe-area-inset-top)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate('/')}>
              <img 
                src="/logo-icon.png" 
                alt="BookingFlows" 
                className="w-10 h-10 rounded-xl object-cover"
              />
              <span className="text-2xl font-bold text-gray-900">BookingFlows</span>
            </div>
            
            {/* Desktop Buttons - Hidden on mobile */}
            <div className="hidden md:flex items-center gap-3">
              <button
                onClick={() => navigate('/marketplace')}
                className="text-sm text-gray-500 hover:text-gray-900 transition-colors px-3 py-2 rounded-full"
                data-testid="supplier-nav-explore"
              >
                Explore experiences <span aria-hidden="true">→</span>
              </button>
              <Button 
                variant="ghost" 
                onClick={() => navigate('/login')}
                className="text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-full"
              >
                Log In
              </Button>
              <Button 
                onClick={() => navigate('/signup')}
                className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-medium px-5 py-2.5 rounded-full shadow-sm"
                data-testid="supplier-nav-signup"
              >
                Sign Up Free
              </Button>
            </div>
            
            {/* Mobile Menu Button - Visible on mobile only */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors"
            >
              {mobileMenuOpen ? (
                <X className="w-6 h-6 text-gray-700" />
              ) : (
                <Menu className="w-6 h-6 text-gray-700" />
              )}
            </button>
          </div>
          
          {/* Mobile Dropdown Menu */}
          {mobileMenuOpen && (
            <div className="md:hidden pb-4 space-y-2 border-t border-gray-100 pt-4">
              <Button 
                variant="ghost" 
                onClick={() => {
                  navigate('/login');
                  setMobileMenuOpen(false);
                }}
                className="w-full btn-landing-ghost justify-start"
              >
                Log In
              </Button>
              <Button 
                onClick={() => {
                  navigate('/signup');
                  setMobileMenuOpen(false);
                }}
                className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-medium py-2.5 rounded-full"
              >
                Sign Up Free
              </Button>
            </div>
          )}
        </div>
      </header>

      {/* Hero Section — refined to match marketplace's airy, minimal aesthetic */}
      <section className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 pb-16 sm:pt-20 sm:pb-24 lg:pt-28 lg:pb-32">
        {/* Soft gradient blobs — give the page depth without feeling heavy.
            Mirrors the marketplace hero's "from-blue-50 via-white to-indigo-50"
            ambient feel. */}
        <div className="absolute inset-0 bg-gradient-to-br from-blue-50/60 via-white to-indigo-50/40 -z-10" aria-hidden="true" />
        <div className="absolute top-10 -left-20 w-96 h-96 bg-blue-200/30 rounded-full blur-3xl -z-10 pointer-events-none" aria-hidden="true" />
        <div className="absolute bottom-0 right-0 w-80 h-80 bg-indigo-200/30 rounded-full blur-3xl -z-10 pointer-events-none" aria-hidden="true" />

        <div className="text-center max-w-4xl mx-auto animate-on-scroll">
          {/* Eyebrow — subtle, not a heavy filled pill */}
          <div className="inline-flex items-center gap-2 text-blue-600 text-xs font-semibold uppercase tracking-[0.2em] mb-6">
            <Sparkles className="w-3.5 h-3.5" />
            Booking Software for Operators
          </div>

          {/* Headline — concise elevator pitch. Mirrors marketplace pattern:
              dark first line, gradient second line. */}
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-gray-900 leading-[1.05] mb-5">
            More bookings.<br />
            <span
              className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 via-blue-500 to-indigo-600 inline-block leading-[1.18] pb-1 whitespace-nowrap"
              aria-live="polite"
              aria-atomic="true"
              data-testid="supplier-hero-rotator"
            >
              {buzzText || '\u00A0'}
            </span>
            <span
              className="inline-block w-[0.08em] h-[0.85em] align-[-0.08em] ml-1 bg-blue-400 animate-pulse"
              aria-hidden="true"
            />
          </h1>

          <p className="text-base sm:text-xl text-gray-600 leading-relaxed max-w-2xl mx-auto mb-9 px-2">
            The all-in-one booking platform built by tour, charter &amp; rental operators —
            for operators. Take bookings 24/7, get paid instantly, run your team.
          </p>

          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button
              onClick={() => { trackSignupStarted('supplier_hero'); navigate('/signup'); }}
              size={null}
              className="btn-supplier-primary bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-medium text-base px-8 py-3.5 rounded-full shadow-lg shadow-blue-500/20"
              data-testid="supplier-hero-signup"
            >
              Start free <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
            <Button
              variant="outline"
              size={null}
              onClick={() => navigate('/meet-the-team')}
              className="text-base px-7 py-3.5 rounded-full border-gray-200 hover:border-gray-300 hover:bg-gray-50 text-gray-700"
              data-testid="supplier-hero-meet-team"
            >
              Meet the team
            </Button>
          </div>

          <p className="text-xs text-gray-400 mt-5 tracking-wide">
            Free setup · No credit card required · Live in 15 minutes
          </p>
        </div>
      </section>

      {/* Trusted-by strip — label left, infinite logo marquee right w/ white fades. */}
      <section className="border-y border-gray-100 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <p className="text-[11px] uppercase tracking-[0.2em] text-gray-400 font-semibold text-center md:text-left shrink-0">
              Powered by best-in-class infrastructure
            </p>
            <div className="w-full md:flex-1 md:max-w-3xl relative overflow-hidden" data-testid="trusted-infra-logos">
              <div className="pointer-events-none absolute inset-y-0 left-0 w-16 sm:w-24 bg-gradient-to-r from-white to-transparent z-10" aria-hidden="true" />
              <div className="pointer-events-none absolute inset-y-0 right-0 w-16 sm:w-24 bg-gradient-to-l from-white to-transparent z-10" aria-hidden="true" />
              <div className="flex w-max animate-infra-marquee">
                <InfraLogoSet />
                <InfraLogoSet ariaHidden />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Customer Story bar removed — case study lives at /customers/boatx
          and is reachable via the "See the BOATX story" hero CTA. Keeps
          the homepage breathing into the feature grid below. */}



      {/* ───────────────────────────────────────────────────────────────
          Operator spotlight carousel — Fun Beach Water Sports (Dubai, UAE;
          the "gone international" flex) leads, then crossfades to Banana
          Boat Rentals (Austin, TX). Auto-advances every 9s; dots to switch.
      ─────────────────────────────────────────────────────────────── */}
      <section className="py-24 bg-white" data-testid="supplier-spotlight-carousel">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid">
            <div
              className={`col-start-1 row-start-1 transition-opacity duration-700 ${spotSlide === 0 ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
              aria-hidden={spotSlide !== 0}
              data-testid="spotlight-slide-fbws"
            >
            <div className="relative rounded-[2rem] overflow-hidden bg-gradient-to-br from-[#050d1f] via-[#0b1d3a] to-black text-white shadow-[0_40px_90px_-35px_rgba(0,0,0,0.65)]">
              {/* dot texture + gold glow */}
              <div className="absolute inset-0 opacity-[0.15] pointer-events-none"
                   style={{ backgroundImage: "radial-gradient(circle at 1px 1px, rgba(251,191,36,0.5) 1px, transparent 0)", backgroundSize: "30px 30px" }} />
              <div className="absolute -top-24 -left-16 w-96 h-96 rounded-full bg-amber-400/15 blur-3xl pointer-events-none" />

              <div className="relative grid lg:grid-cols-2 gap-12 lg:gap-16 items-center p-7 sm:p-10 lg:p-14">
                {/* Left — floating collage */}
                <div className="relative order-2 lg:order-1">
                  <div className="grid grid-cols-2 grid-rows-3 gap-3 aspect-[4/5]">
                    <img
                      src="/images/fbws-jetski.webp"
                      alt="A Fun Beach Water Sports jet ski in front of the Burj Al Arab, Dubai"
                      className="col-span-2 row-span-2 w-full h-full object-cover rounded-2xl shadow-xl ring-1 ring-white/10"
                      loading="lazy"
                      decoding="async"
                    />
                    <img
                      src="/images/fbws-jetcar.webp"
                      alt="The Fun Beach Water Sports jet car on the water off the Dubai coastline"
                      className="w-full h-full object-cover rounded-2xl shadow-xl ring-1 ring-white/10"
                      loading="lazy"
                      decoding="async"
                    />
                    <img
                      src="/images/fbws-flyboard.webp"
                      alt="A flyboard session with Fun Beach Water Sports in Dubai"
                      className="w-full h-full object-cover rounded-2xl shadow-xl ring-1 ring-white/10"
                      loading="lazy"
                      decoding="async"
                    />
                  </div>

                  {/* floating Fun Beach logo — top-left */}
                  <div className="absolute -top-3 -left-2 sm:-left-4 z-10 bg-white rounded-xl ring-1 ring-black/10 px-2.5 py-1.5 shadow-2xl">
                    <img src="/images/fbws-logo.webp"
                         alt="Fun Beach Water Sports" className="h-8 sm:h-10 w-auto object-contain" loading="lazy" />
                  </div>
                  {/* floating location pill */}
                  <div className="absolute -top-3 -right-2 sm:-right-4 bg-amber-400 text-black rounded-xl px-3.5 py-2 font-bold text-sm shadow-2xl flex items-center gap-1.5">
                    <MapPin className="w-4 h-4" /> Dubai · UAE
                  </div>
                  {/* floating international pill */}
                  <div className="absolute -bottom-4 -right-2 sm:-right-5 bg-white text-gray-900 rounded-2xl shadow-2xl px-4 py-3 flex items-center gap-2.5 max-w-[260px]">
                    <Globe className="w-6 h-6 text-[#0b1d3a] shrink-0" />
                    <div className="text-xs leading-tight">
                      <div className="font-semibold">Two continents, one platform</div>
                      <div className="text-gray-500">AED processed · GST (UTC+4)</div>
                    </div>
                  </div>
                  {/* floating review pill */}
                  <div className="absolute -bottom-4 -left-2 sm:-left-5 bg-white text-gray-900 rounded-2xl shadow-2xl px-4 py-3 flex items-center gap-2.5 max-w-[260px]">
                    <div className="flex">
                      {[0,1,2,3,4].map(i => <Star key={i} className="w-3 h-3 fill-amber-400 text-amber-400" />)}
                    </div>
                    <div className="text-xs leading-tight">
                      <div className="font-semibold">1,059 Google reviews</div>
                      <div className="text-gray-500">5.0 · Dubai, UAE</div>
                    </div>
                  </div>
                </div>

                {/* Right — copy */}
                <div className="order-1 lg:order-2">
                  <div className="flex justify-center lg:justify-start mb-6">
                    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-400/15 border border-amber-300/25 text-[11px] font-semibold uppercase tracking-widest text-amber-200" data-testid="fbws-international-badge">
                      <Globe className="w-3.5 h-3.5 text-amber-300" /> BookingFlows goes international
                    </div>
                  </div>

                  <h2 className="text-3xl sm:text-4xl lg:text-[2.75rem] font-bold tracking-tight leading-[1.08] mb-4">
                    Welcoming <span className="bg-gradient-to-r from-amber-300 to-yellow-400 bg-clip-text text-transparent">Fun Beach Water Sports</span> — Dubai, United Arab Emirates.
                  </h2>
                  <p className="text-base sm:text-lg text-gray-300 leading-relaxed mb-7 max-w-xl">
                    Our first operator on the other side of the planet. Fun Beach runs a full
                    watersports fleet on the Dubai coastline — jet skis, the jet car, flyboards —
                    and the whole operation now runs on BookingFlows: bookings in dirhams,
                    schedules on Gulf Standard Time, same platform.
                  </p>

                  <div className="flex flex-wrap gap-2.5 mb-6">
                    {[
                      { icon: Waves, label: 'Jet ski fleet' },
                      { icon: Zap,   label: 'Jet car & flyboard' },
                      { icon: Star,  label: '1,059 five-star reviews' },
                    ].map(({ icon: Icon, label }) => (
                      <span key={label} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/10 border border-white/10 text-sm text-gray-200">
                        <Icon className="w-3.5 h-3.5 text-amber-300" /> {label}
                      </span>
                    ))}
                  </div>

                  <div className="flex flex-wrap items-center gap-x-5 gap-y-2 mb-8" data-testid="fbws-intl-chips">
                    {[
                      'AED payments, natively processed',
                      'Gulf Standard Time (UTC+4) scheduling',
                      'UAE-ready compliance',
                    ].map((t) => (
                      <span key={t} className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-400">
                        <Check className="w-3.5 h-3.5 text-amber-300" /> {t}
                      </span>
                    ))}
                  </div>

                  <div className="flex flex-wrap items-center gap-3">
                    <button
                      type="button"
                      onClick={() => navigate('/customers/fun-beach-water-sports')}
                      data-testid="fbws-spotlight-story-btn"
                      className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-amber-400 text-black font-semibold hover:bg-amber-300 transition-colors"
                    >
                      Read their story <ArrowRight className="w-4 h-4" />
                    </button>
                    <a
                      href="https://funbeachwatersports.com/"
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="fbws-spotlight-visit-btn"
                      className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-white/10 hover:bg-white/15 border border-white/15 text-white font-medium transition-colors backdrop-blur"
                    >
                      Visit funbeachwatersports.com <Globe className="w-4 h-4" />
                    </a>
                  </div>
                </div>
              </div>
            </div>
            </div>
            <div
              className={`col-start-1 row-start-1 transition-opacity duration-700 ${spotSlide === 1 ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
              aria-hidden={spotSlide !== 1}
              data-testid="spotlight-slide-bbr"
            >
            <div className="relative rounded-[2rem] overflow-hidden bg-gradient-to-br from-neutral-950 via-neutral-900 to-black text-white shadow-[0_40px_90px_-35px_rgba(0,0,0,0.65)]">
              {/* dot texture + yellow glow */}
              <div className="absolute inset-0 opacity-[0.15] pointer-events-none"
                   style={{ backgroundImage: "radial-gradient(circle at 1px 1px, rgba(255,221,51,0.5) 1px, transparent 0)", backgroundSize: "30px 30px" }} />
              <div className="absolute -top-24 -right-16 w-96 h-96 rounded-full bg-yellow-400/15 blur-3xl pointer-events-none" />

              <div className="relative grid lg:grid-cols-2 gap-12 lg:gap-16 items-center p-7 sm:p-10 lg:p-14">
                {/* Left — copy */}
                <div>
                  <div className="flex justify-center lg:justify-start mb-6">
                    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-yellow-400/15 border border-yellow-300/25 text-[11px] font-semibold uppercase tracking-widest text-yellow-200">
                      <Star className="w-3.5 h-3.5 fill-yellow-300 text-yellow-300" /> Newest on BookingFlows
                    </div>
                  </div>

                  <h2 className="text-3xl sm:text-4xl lg:text-[2.75rem] font-bold tracking-tight leading-[1.08] mb-4">
                    Welcoming <span className="bg-gradient-to-r from-yellow-300 to-amber-400 bg-clip-text text-transparent">Banana Boat Rentals</span> — Austin's largest party-boat fleet.
                  </h2>
                  <p className="text-base sm:text-lg text-gray-300 leading-relaxed mb-7 max-w-xl">
                    We're proud to power Austin's biggest fleet. 15+ boats across Lake Austin
                    and Lake Travis now run on BookingFlows — paired with a clean, modern
                    website and local SEO we built from the ground up. Thank you for trusting
                    us with the operation.
                  </p>

                  <div className="flex flex-wrap gap-2.5 mb-8">
                    {[
                      { icon: Anchor, label: '15+ party boats' },
                      { icon: Star,   label: '500+ 5-star reviews' },
                      { icon: MapPin, label: 'Lake Austin & Travis' },
                    ].map(({ icon: Icon, label }) => (
                      <span key={label} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/10 border border-white/10 text-sm text-gray-200">
                        <Icon className="w-3.5 h-3.5 text-yellow-300" /> {label}
                      </span>
                    ))}
                  </div>

                  <div className="flex flex-wrap items-center gap-3">
                    <button
                      type="button"
                      onClick={() => navigate('/customers/banana-boat-rentals')}
                      data-testid="bbr-spotlight-story-btn"
                      className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-yellow-400 text-black font-semibold hover:bg-yellow-300 transition-colors"
                    >
                      Read their story <ArrowRight className="w-4 h-4" />
                    </button>
                    <a
                      href="https://www.bananaboatrentals.com/"
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="bbr-spotlight-visit-btn"
                      className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-white/10 hover:bg-white/15 border border-white/15 text-white font-medium transition-colors backdrop-blur"
                    >
                      Visit bananaboatrentals.com <Globe className="w-4 h-4" />
                    </a>
                  </div>
                </div>

                {/* Right — floating collage */}
                <div className="relative">
                  <div className="grid grid-cols-2 grid-rows-3 gap-3 aspect-[4/5]">
                    <img
                      src={proxyImg("https://1iynnenkjdavmviy.public.blob.vercel-storage.com/bbr_hero_bridge.jpg", 800, 80)}
                      alt="Banana Boat Rentals guests with the Pennybacker Bridge on Lake Austin"
                      className="col-span-2 row-span-2 w-full h-full object-cover rounded-2xl shadow-xl ring-1 ring-white/10"
                      loading="lazy"
                      decoding="async"
                    />
                    <img
                      src={proxyImg("https://1iynnenkjdavmviy.public.blob.vercel-storage.com/bbr_party_group.jpg", 500, 80)}
                      alt="A large group celebrating on a Banana Boat Rentals party boat"
                      className="w-full h-full object-cover rounded-2xl shadow-xl ring-1 ring-white/10"
                      loading="lazy"
                      decoding="async"
                    />
                    <img
                      src={proxyImg("https://1iynnenkjdavmviy.public.blob.vercel-storage.com/bbr_yellow_pontoon.jpg", 500, 80)}
                      alt="A bright yellow Banana Boat Rentals pontoon cruising Lake Austin"
                      className="w-full h-full object-cover rounded-2xl shadow-xl ring-1 ring-white/10"
                      loading="lazy"
                      decoding="async"
                    />
                  </div>

                  {/* floating Banana Boat Rentals logo — top-left */}
                  <div className="absolute -top-3 -left-2 sm:-left-4 z-10 bg-black rounded-xl ring-1 ring-white/15 px-2.5 py-1.5 shadow-2xl">
                    <img src="https://1iynnenkjdavmviy.public.blob.vercel-storage.com/bbr_logo.png"
                         alt="Banana Boat Rentals" className="h-7 sm:h-9 w-auto object-contain" loading="lazy" />
                  </div>
                  {/* floating fleet pill */}
                  <div className="absolute -top-3 -right-2 sm:-right-4 bg-yellow-400 text-black rounded-xl px-3.5 py-2 font-bold text-sm shadow-2xl flex items-center gap-1.5">
                    <Anchor className="w-4 h-4" /> 15+ boats
                  </div>
                  {/* floating review pill */}
                  <div className="absolute -bottom-4 -left-2 sm:-left-5 bg-white text-gray-900 rounded-2xl shadow-2xl px-4 py-3 flex items-center gap-2.5 max-w-[260px]">
                    <div className="flex">
                      {[0,1,2,3,4].map(i => <Star key={i} className="w-3 h-3 fill-amber-400 text-amber-400" />)}
                    </div>
                    <div className="text-xs leading-tight">
                      <div className="font-semibold">500+ Google reviews</div>
                      <div className="text-gray-500">Austin's largest fleet</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            </div>
            <div
              className={`col-start-1 row-start-1 transition-opacity duration-700 ${spotSlide === 2 ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
              aria-hidden={spotSlide !== 2}
              data-testid="spotlight-slide-boatx"
            >
              <div className="relative rounded-[2rem] overflow-hidden bg-gradient-to-br from-[#031619] via-[#06333a] to-black text-white shadow-[0_40px_90px_-35px_rgba(0,0,0,0.65)]">
                {/* dot texture + teal glow */}
                <div className="absolute inset-0 opacity-[0.15] pointer-events-none"
                     style={{ backgroundImage: "radial-gradient(circle at 1px 1px, rgba(45,212,191,0.5) 1px, transparent 0)", backgroundSize: "30px 30px" }} />
                <div className="absolute -top-24 -right-16 w-96 h-96 rounded-full bg-teal-400/15 blur-3xl pointer-events-none" />

                <div className="relative grid lg:grid-cols-2 gap-12 lg:gap-16 items-center p-7 sm:p-10 lg:p-14">
                  {/* Left — copy */}
                  <div>
                    <div className="flex justify-center lg:justify-start mb-6">
                      <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-teal-400/15 border border-teal-300/25 text-[11px] font-semibold uppercase tracking-widest text-teal-200" data-testid="boatx-spotlight-badge">
                        <Anchor className="w-3.5 h-3.5 text-teal-300" /> Powered end to end
                      </div>
                    </div>

                    <h2 className="text-3xl sm:text-4xl lg:text-[2.75rem] font-bold tracking-tight leading-[1.08] mb-4">
                      Welcoming <span className="bg-gradient-to-r from-teal-300 to-cyan-400 bg-clip-text text-transparent">BOATX</span> — custom party pontoons on Lake Austin &amp; Lake Travis.
                    </h2>
                    <p className="text-base sm:text-lg text-gray-300 leading-relaxed mb-7 max-w-xl">
                      1,037+ five-star Google reviews and the wildest boats on the water —
                      double-deckers, slides, even a leopard-print pontoon. From boatx.io
                      (designed and built by BookingFlows) to the dock, every booking,
                      waiver, and payment runs on the platform.
                    </p>

                    <div className="flex flex-wrap gap-2.5 mb-6">
                      {[
                        { icon: Anchor, label: 'Custom double-deckers' },
                        { icon: Star,   label: '1,037+ 5-star reviews' },
                        { icon: MapPin, label: 'Lake Austin & Travis' },
                      ].map(({ icon: Icon, label }) => (
                        <span key={label} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/10 border border-white/10 text-sm text-gray-200">
                          <Icon className="w-3.5 h-3.5 text-teal-300" /> {label}
                        </span>
                      ))}
                    </div>

                    <div className="flex flex-wrap items-center gap-x-5 gap-y-2 mb-8" data-testid="boatx-chips">
                      {[
                        'Website + bookings, one team',
                        'Embedded widget live on boatx.io',
                        'Gift cards & group events',
                      ].map((t) => (
                        <span key={t} className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-400">
                          <Check className="w-3.5 h-3.5 text-teal-300" /> {t}
                        </span>
                      ))}
                    </div>

                    <div className="flex flex-wrap items-center gap-3">
                      <button
                        type="button"
                        onClick={() => navigate('/customers/boatx')}
                        data-testid="boatx-spotlight-story-btn"
                        className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-teal-400 text-black font-semibold hover:bg-teal-300 transition-colors"
                      >
                        Read their story <ArrowRight className="w-4 h-4" />
                      </button>
                      <a
                        href="https://www.boatx.io/"
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="boatx-spotlight-visit-btn"
                        className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-white/10 hover:bg-white/15 border border-white/15 text-white font-medium transition-colors backdrop-blur"
                      >
                        Visit boatx.io <Globe className="w-4 h-4" />
                      </a>
                    </div>
                  </div>

                  {/* Right — floating collage */}
                  <div className="relative">
                    <div className="grid grid-cols-2 grid-rows-3 gap-3 aspect-[4/5]">
                      <img
                        src="/images/boatx-hero.webp"
                        alt="A BOATX double-decker party pontoon loaded with a birthday crew on Lake Austin"
                        className="col-span-2 row-span-2 w-full h-full object-cover rounded-2xl shadow-xl ring-1 ring-white/10"
                        loading="lazy"
                        decoding="async"
                      />
                      <img
                        src="/images/boatx-leopard.webp"
                        alt="The BOATX leopard-print pontoon cruising Lake Austin"
                        className="w-full h-full object-cover rounded-2xl shadow-xl ring-1 ring-white/10"
                        loading="lazy"
                        decoding="async"
                      />
                      <img
                        src="/images/boatx-sunset.webp"
                        alt="Sunset aboard a BOATX pontoon on Lake Travis"
                        className="w-full h-full object-cover rounded-2xl shadow-xl ring-1 ring-white/10"
                        loading="lazy"
                        decoding="async"
                      />
                    </div>

                    {/* floating BOATX logo — top-left */}
                    <div className="absolute -top-3 -left-2 sm:-left-4 z-10 bg-[#052e33] rounded-xl ring-1 ring-white/15 px-3 py-2 shadow-2xl">
                      <img src="/images/boatx-logo.webp"
                           alt="BOATX" className="h-6 sm:h-7 w-auto object-contain" loading="lazy" />
                    </div>
                    {/* floating location pill */}
                    <div className="absolute -top-3 -right-2 sm:-right-4 bg-teal-400 text-black rounded-xl px-3.5 py-2 font-bold text-sm shadow-2xl flex items-center gap-1.5">
                      <MapPin className="w-4 h-4" /> Austin · TX
                    </div>
                    {/* floating review pill */}
                    <div className="absolute -bottom-4 -left-2 sm:-left-5 bg-white text-gray-900 rounded-2xl shadow-2xl px-4 py-3 flex items-center gap-2.5 max-w-[260px]">
                      <div className="flex">
                        {[0,1,2,3,4].map(i => <Star key={i} className="w-3 h-3 fill-amber-400 text-amber-400" />)}
                      </div>
                      <div className="text-xs leading-tight">
                        <div className="font-semibold">1,037+ Google reviews</div>
                        <div className="text-gray-500">5.0 · Lake Austin</div>
                      </div>
                    </div>
                    {/* floating website pill */}
                    <div className="absolute -bottom-4 -right-2 sm:-right-5 bg-white text-gray-900 rounded-2xl shadow-2xl px-4 py-3 flex items-center gap-2.5 max-w-[260px]">
                      <Globe className="w-6 h-6 text-[#052e33] shrink-0" />
                      <div className="text-xs leading-tight">
                        <div className="font-semibold">boatx.io</div>
                        <div className="text-gray-500">Built by BookingFlows</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* carousel dots */}
          <div className="flex items-center justify-center gap-2.5 mt-10">
            {[
              ['spotlight-dot-0', 'Show Fun Beach Water Sports (Dubai, UAE)'],
              ['spotlight-dot-1', 'Show Banana Boat Rentals (Austin, TX)'],
              ['spotlight-dot-2', 'Show BOATX (Lake Austin & Lake Travis, TX)'],
            ].map(([tid, label], i) => (
              <button
                key={tid}
                type="button"
                onClick={() => setSpotSlide(i)}
                data-testid={tid}
                aria-label={label}
                className={`h-2 rounded-full transition-all duration-300 ${spotSlide === i ? 'w-8 bg-gray-900' : 'w-2 bg-gray-300 hover:bg-gray-400'}`}
              />
            ))}
          </div>
        </div>
      </section>

      {/* ───────────────────────────────────────────────────────────────
          Marine weather — tide, wind, solar & moon built into the platform.
          Captains see it in the dashboard; customers get a trip-day report.
      ─────────────────────────────────────────────────────────────── */}
      <section className="py-24 bg-white" data-testid="supplier-weather-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            {/* Left — copy */}
            <div className="animate-on-scroll">
              <div className="inline-flex items-center gap-2 text-sky-600 text-xs font-semibold uppercase tracking-[0.2em] mb-4">
                <CloudSun className="w-3.5 h-3.5" />
                Marine weather
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 tracking-tight leading-[1.1]">
                Weather, tide and moon.
                <span className="block text-gray-400">Built into your operation.</span>
              </h2>
              <p className="mt-5 text-lg text-gray-600 leading-relaxed max-w-xl">
                Mariners live by the conditions — so we put them where your crew already
                works. One glance from the dashboard, one report in every customer's inbox.
              </p>

              <div className="mt-7 space-y-3.5">
                {[
                  { Icon: Wind, title: 'Live conditions on your dashboard', desc: 'Temperature, wind in knots, gusts and hourly outlook for your exact operating area.' },
                  { Icon: Waves, title: 'Tide & solar aware', desc: 'High and low tide times, sunrise, sunset and UV — your captains see it all before first departure.' },
                  { Icon: Mail, title: 'Morning crew briefing', desc: 'The whole team gets the day\u2019s outlook emailed automatically, every morning.' },
                  { Icon: Moon, title: 'Customers get a trip-day report', desc: 'Included with every booking — a clean forecast for their exact departure time, moon phase and all.' },
                ].map((f) => (
                  <div key={f.title} className="flex items-start gap-3.5">
                    <span className="w-8 h-8 rounded-full bg-sky-50 text-sky-600 flex items-center justify-center shrink-0 mt-0.5">
                      <f.Icon className="w-4 h-4" strokeWidth={1.75} />
                    </span>
                    <div>
                      <span className="font-semibold text-gray-900 text-sm">{f.title}</span>
                      <span className="text-gray-500 text-sm"> — {f.desc}</span>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-8 flex flex-wrap items-center gap-4">
                <button
                  onClick={() => { trackSignupStarted('supplier_weather_section'); navigate('/signup'); }}
                  data-testid="supplier-weather-signup"
                  className="group inline-flex items-center gap-2 text-sm font-semibold text-sky-600 hover:text-sky-700 transition-colors"
                >
                  Bring weather aboard
                  <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                </button>
                <span className="text-xs text-gray-400 font-medium">
                  Included on every plan. No add-ons.
                </span>
              </div>
            </div>

            {/* Right — weather cards visual */}
            <div className="animate-on-scroll" aria-hidden="true">
              <img
                src="/images/bf-weather-mockup.webp"
                alt="BookingFlows marine weather — tide, wind, moon and solar conditions"
                className="w-full block"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Built by an operator — founder credentials + global advisory.
          The human, trust-building moment of the page. */}
      <section className="py-24 bg-gradient-to-b from-white via-blue-50/60 to-white relative overflow-hidden">
        <div className="absolute top-24 left-0 w-72 h-72 bg-blue-200/30 rounded-full blur-3xl -z-0" aria-hidden="true" />
        <div className="absolute bottom-24 right-0 w-72 h-72 bg-indigo-200/30 rounded-full blur-3xl -z-0" aria-hidden="true" />

        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid md:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Founder image */}
            <div className="order-2 md:order-1">
              <div className="relative max-w-md mx-auto md:mx-0">
                <img
                  src="/images/founder/founder.jpg"
                  alt="BookingFlows founder"
                  className="rounded-3xl shadow-2xl shadow-blue-500/10 w-full object-cover aspect-square"
                />
                {/* Floating "at the helm" accent — real, not stock */}
                <div className="absolute -top-3 -left-2 sm:-top-5 sm:-left-6 w-[4.5rem] sm:w-24 -rotate-[5deg] z-10">
                  <img
                    src={proxyImg("https://customer-assets.emergentagent.com/job_c73baa61-619c-4b73-9105-ad2ee2a3c85f/artifacts/usx6igta_IMG_7883.jpg", 400, 80)}
                    alt="Ryan at the helm"
                    className="w-full aspect-[3/4] object-cover rounded-2xl shadow-xl ring-4 ring-white"
                    loading="lazy"
                    decoding="async"
                  />
                </div>
                {/* Floating "boat lights + whip cream" accent — top right */}
                <div className="absolute -top-3 -right-2 sm:-top-5 sm:-right-6 w-[4.5rem] sm:w-24 rotate-[5deg] z-10">
                  <img
                    src={proxyImg("https://customer-assets.emergentagent.com/job_c73baa61-619c-4b73-9105-ad2ee2a3c85f/artifacts/z12c9tfo_Captain%20Ryan.remini-enhanced%20%281%29.JPG", 400, 80)}
                    alt="Ryan smiling on the boat"
                    className="w-full aspect-[3/4] object-cover rounded-2xl shadow-xl ring-4 ring-white"
                    loading="lazy"
                    decoding="async"
                  />
                </div>
                {/* Floating "racing under the Dubai skyline" accent — bottom left */}
                <div className="absolute -bottom-4 -left-2 sm:-bottom-6 sm:-left-7 w-[4.5rem] sm:w-24 -rotate-[4deg] z-10">
                  <img
                    src="/images/founder/racing-dubai.jpg"
                    alt="Sailboats racing under the Dubai skyline"
                    className="w-full aspect-[3/4] object-cover rounded-2xl shadow-xl ring-4 ring-white"
                    loading="lazy"
                    decoding="async"
                  />
                  <span className="absolute bottom-1 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full bg-slate-900/60 backdrop-blur-sm px-1.5 py-0.5 text-[7px] sm:text-[8px] font-semibold text-white tracking-wide" data-testid="founder-racing-dubai-tag">
                    Racing, Dubai
                  </span>
                </div>
                {/* Floating cert badge */}
                <div className="absolute -bottom-4 -right-2 sm:-bottom-5 sm:-right-3 bg-white rounded-xl sm:rounded-2xl shadow-lg border border-gray-100 px-2.5 py-1.5 sm:px-4 sm:py-3 flex items-center gap-2 sm:gap-3">
                  <div className="w-7 h-7 sm:w-9 sm:h-9 rounded-full bg-gradient-to-br from-blue-600 to-indigo-600 flex items-center justify-center flex-shrink-0">
                    <Shield className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-white" />
                  </div>
                  <div>
                    <div className="text-[9px] sm:text-[10px] uppercase tracking-wider text-gray-400 font-semibold">Licensed Captain</div>
                    <div className="text-[11px] sm:text-sm font-semibold text-gray-900">USCG · RYA · MCA · IYT · ASA</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Copy */}
            <div className="order-1 md:order-2">
              <div className="inline-flex items-center gap-2 text-blue-600 text-xs font-semibold uppercase tracking-[0.2em] mb-5">
                <Star className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />
                Built by an operator
              </div>
              <h3 className="text-4xl md:text-5xl font-bold tracking-tight text-gray-900 leading-[1.1] mb-5">
                Built by a captain.<br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">
                  Reviewed by operators worldwide.
                </span>
              </h3>
              <p className="text-base sm:text-lg text-gray-600 leading-relaxed mb-7">
                BookingFlows isn't another generic SaaS. It's built by a licensed charter captain
                with international qualifications — and shaped by operators in Florida, the
                Mediterranean, the UAE and the Caribbean. Every feature solves a real problem
                someone with calloused hands has actually had.
              </p>

              {/* Cert pills */}
              <div className="flex flex-wrap gap-2 mb-7">
                {['USCG', 'RYA', 'MCA', 'IYT', 'ASA'].map((cert) => (
                  <span
                    key={cert}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-gray-200 text-xs font-semibold text-gray-700 shadow-sm"
                  >
                    <Shield className="w-3 h-3 text-blue-600" />
                    {cert}
                  </span>
                ))}
              </div>

              {/* Stat row — flat */}
              <div className="grid grid-cols-3 gap-3 sm:gap-5">
                <div>
                  <div className="text-2xl font-bold text-gray-900">16+</div>
                  <div className="text-[11px] uppercase tracking-wider text-gray-500 mt-0.5">Certs, Licenses &amp; Endorsements</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-gray-900">Lab</div>
                  <div className="text-[11px] uppercase tracking-wider text-gray-500 mt-0.5">SWE Background</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-gray-900">100%</div>
                  <div className="text-[11px] uppercase tracking-wider text-gray-500 mt-0.5">Operator-Built</div>
                </div>
              </div>

              {/* Meet the team link */}
              <button
                onClick={() => navigate('/meet-the-team')}
                className="group mt-7 inline-flex items-center gap-2 text-blue-600 font-semibold hover:text-blue-700 transition-colors"
                data-testid="founder-meet-team-link"
              >
                Meet the team
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </button>

            </div>
          </div>
        </div>
      </section>
      {/* ───────────────────────────────────────────────────────────────
          The nutshell — what BookingFlows is, in three sentences.
      ─────────────────────────────────────────────────────────────── */}
      <section className="py-24 bg-white" data-testid="supplier-nutshell-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center justify-center gap-2 text-blue-600 text-xs font-semibold uppercase tracking-[0.2em] mb-6">
            <Anchor className="w-3.5 h-3.5" />
            BookingFlows, in a nutshell
          </div>
          <img
            src="/images/bf-overview.webp"
            alt="BookingFlows platform overview — reservations, waivers, weather, fleet, finance, crew, payments, analytics, AI and advertising around one dashboard"
            loading="lazy"
            data-testid="nutshell-section-visual"
            className="w-full block max-w-5xl mx-auto mb-14"
          />
          <h3 className="text-4xl md:text-5xl font-bold tracking-tight text-gray-900 leading-[1.12] mb-8">
            Operations-grade booking software{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">for mariners.</span>
          </h3>
          <p className="text-lg sm:text-xl text-gray-600 leading-relaxed max-w-3xl mx-auto">
            Reservations, waivers, weather, crew, payments, analytics and AI — one platform
            that runs the whole operation. Built for charter companies, boat rentals, and
            everyone who works on the water.
          </p>
        </div>
      </section>

      {/* ───────────────────────────────────────────────────────────────
          HELM Intelligence by BookingFlows — flagship analytics suite.
          Placed top-of-page per founder: market the outcome, not the
          methodology. All numbers below are illustrative.
      ─────────────────────────────────────────────────────────────── */}
      {/* 💚 QuickBooks Online sync — LIVE. Official Intuit QuickBooks Online
          integration (production keys). Positioning per founder: official,
          primed and professional — integrated, not overclaiming partnership. */}
      <section className="py-24 bg-white" data-testid="supplier-quickbooks-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center justify-center gap-2 text-[#2CA01C] text-xs font-semibold uppercase tracking-[0.2em] mb-6" data-testid="quickbooks-official-badge">
            <RefreshCw className="w-3.5 h-3.5" />
            Official integration · Intuit QuickBooks Online
          </div>
          <img
            src="/images/bf-quickbooks-connect.webp"
            alt="BookingFlows connected to Intuit QuickBooks Online — bookings, waivers, weather and crew on one side, invoices, banking and reports on the other"
            loading="lazy"
            data-testid="quickbooks-section-visual"
            className="w-full block max-w-3xl mx-auto mb-14"
          />
          <h3 className="text-4xl md:text-5xl font-bold tracking-tight text-gray-900 leading-[1.12] mb-8">
            Your books,{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#2CA01C] to-emerald-500">on autopilot.</span>
          </h3>
          <p className="text-lg sm:text-xl text-gray-600 leading-relaxed max-w-3xl mx-auto mb-10">
            BookingFlows is officially integrated with Intuit QuickBooks Online. Connect your
            company once — your crew's expense uploads post as categorized Purchases, paid
            bookings roll up into clean daily Sales Receipts, and every line lands in your own
            chart of accounts. Synced nightly, nothing duplicated, no month-end retyping.
          </p>
          <div className="grid sm:grid-cols-3 gap-4 max-w-3xl mx-auto mb-10 text-left" data-testid="quickbooks-feature-grid">
            {[
              ['Crew-logged expenses', 'Captains snap receipts at the dock — QuickBooks gets a categorized Purchase that night.'],
              ['Daily revenue receipts', 'Paid bookings roll into one Sales Receipt per day. Clean P&L, zero retyping.'],
              ['Your accounts, respected', 'Categories map to your own chart of accounts — in USD, AED or any home currency.'],
            ].map(([t, d]) => (
              <div key={t} className="rounded-xl border border-gray-100 bg-gray-50/60 p-4">
                <div className="flex items-center gap-2 mb-1.5">
                  <Check className="w-4 h-4 text-[#2CA01C] flex-shrink-0" />
                  <span className="text-sm font-bold text-gray-900">{t}</span>
                </div>
                <p className="text-[13px] text-gray-500 leading-relaxed">{d}</p>
              </div>
            ))}
          </div>
          <button
            onClick={() => navigate('/blog/quickbooks-sync')}
            className="group inline-flex items-center gap-2 text-sm font-semibold text-[#2CA01C] hover:text-[#108000] transition-colors"
            data-testid="quickbooks-section-cta"
          >
            See how the sync works
            <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
          </button>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-x-6 gap-y-2" data-testid="quickbooks-trust-row">
            {[
              [Lock, 'Secure OAuth connection'],
              [Shield, 'Encrypted tokens, bank-grade security'],
              [Check, 'Disconnect anytime, from either side'],
            ].map(([Icon, t]) => (
              <span key={t} className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-500">
                <Icon className="w-3.5 h-3.5 text-[#2CA01C]" />
                {t}
              </span>
            ))}
          </div>
          <div className="mt-4">
            <a
              href="https://quickbooks.intuit.com/partners/affiliates/?cid=par_pim_Qxq0keD8r71c"
              target="_blank"
              rel="noopener noreferrer sponsored"
              data-testid="quickbooks-affiliate-link"
              className="text-sm text-gray-500 hover:text-[#2CA01C] underline underline-offset-4 transition-colors"
            >
              Don&apos;t use QuickBooks yet? Get QuickBooks Online &rarr;
            </a>
          </div>
          <p className="mt-10 text-[11px] text-gray-400">
            Intuit and QuickBooks are registered trademarks of Intuit Inc. Used with permission.
          </p>
        </div>
      </section>

      {/* Embeddable Booking Widget Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Text Content - Centered on top */}
          <div className="text-center mb-12 max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-2 text-blue-600 text-xs font-semibold uppercase tracking-[0.2em] mb-4">
              <Code className="w-3.5 h-3.5" />
              Your Website
            </div>
            <h3 className="text-4xl md:text-5xl font-bold tracking-tight text-gray-900 leading-[1.1] mb-4">
              Embed bookings on{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">your own site.</span>
            </h3>
            <p className="text-lg text-gray-500 mb-6 leading-relaxed">
              Customers book directly from your website. No redirects. No friction.
            </p>
            <div className="flex flex-wrap justify-center gap-x-8 gap-y-3">
              <div className="flex items-center gap-2">
                <Check className="w-5 h-5 text-purple-600 flex-shrink-0" />
                <span className="text-gray-700">Copy & paste install</span>
              </div>
              <div className="flex items-center gap-2">
                <Check className="w-5 h-5 text-purple-600 flex-shrink-0" />
                <span className="text-gray-700">Brand colors</span>
              </div>
              <div className="flex items-center gap-2">
                <Check className="w-5 h-5 text-purple-600 flex-shrink-0" />
                <span className="text-gray-700">Mobile-responsive</span>
              </div>
              <div className="flex items-center gap-2">
                <Check className="w-5 h-5 text-purple-600 flex-shrink-0" />
                <span className="text-gray-700">Real-time sync</span>
              </div>
            </div>
          </div>

          {/* Embed renders — one snippet fanning out to every surface, then
              the widget living on a real operator site (white-on-white) */}
          <div className="max-w-5xl mx-auto">
            <img
              src="/images/bf-embeds-2.webp"
              alt="One embed snippet powering BookingFlows booking widgets across desktop, mobile and calendar views"
              className="w-full block"
              loading="lazy"
              data-testid="embed-section-visual-1"
            />
            <img
              src="/images/bf-embeds.webp"
              alt="BookingFlows widget embedded on an operator's own website, on laptop and phone, with the embed code alongside"
              className="w-full block mt-2 sm:mt-4"
              loading="lazy"
              data-testid="embed-section-visual-2"
            />
          </div>
        </div>
      </section>

      {/* Waiver Management — converted from dark to light to break the
          dark-stack rhythm. The Shareable Customer Tickets section below
          covers the customer-facing side; this is the legal/audit angle. */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16 max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-2 text-blue-600 text-xs font-semibold uppercase tracking-[0.2em] mb-4">
              <Shield className="w-3.5 h-3.5" />
              Legal Protection
            </div>
            <h3 className="text-4xl md:text-5xl font-bold tracking-tight text-gray-900 leading-[1.1] mb-4">
              Waivers that{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">actually hold up.</span>
            </h3>
            <p className="text-lg text-gray-500 leading-relaxed">
              Device fingerprints, IP capture, and audit trails — so you have real proof if you ever need it.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="space-y-7 md:order-2">
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center flex-shrink-0">
                  <Fingerprint className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-1">Device Verification</h4>
                  <p className="text-sm text-gray-500 leading-relaxed">
                    Capture device fingerprints, IP addresses, and browser info automatically when customers sign. Full audit trail for legal protection.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center flex-shrink-0">
                  <Lock className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-1">PIN-Based Tickets</h4>
                  <p className="text-sm text-gray-500 leading-relaxed">
                    Guides get a unique PIN for each trip. Customers enter the PIN to access their ticket — guides instantly know who signed.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center flex-shrink-0">
                  <FileText className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-1">Custom Waiver Text</h4>
                  <p className="text-sm text-gray-500 leading-relaxed">
                    Write your own waiver text. Multi-signature support for groups. Customers sign on their phone before the trip.
                  </p>
                </div>
              </div>
            </div>

            <div className="relative md:order-1">
              <img
                src="/images/bf-waivers.webp"
                alt="BookingFlows digital waivers — signed and verified records on tablet and phone"
                loading="lazy"
                data-testid="waivers-section-visual"
                className="w-full block"
              />
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 bg-white" data-testid="supplier-helm-section">
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Left — copy */}
            <div>
              <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[#EBF2FF] border border-[#2563EB]/15 mb-6">
                <img src="/bf-intelligence-logo.png" alt="HELM Intelligence" className="w-4 h-4 object-contain" />
                <span className="text-[11px] font-bold uppercase tracking-[0.14em] text-[#2563EB]">New · Included free with BookingFlows</span>
              </div>

              <h2 className="text-3xl sm:text-4xl lg:text-[2.75rem] font-bold tracking-tight leading-[1.08] text-gray-900 mb-2">
                Meet HELM <span className="italic">Intelligence</span>.
              </h2>
              <p className="text-sm font-semibold text-gray-400 tracking-wide mb-5">by BookingFlows</p>
              <p className="text-base sm:text-lg text-gray-600 leading-relaxed mb-8 max-w-xl">
                Your booking data, decoded. HELM reads every trip you run and turns it
                into clear moves — what each boat really earns, which services sell,
                when demand peaks, and who keeps coming back.
              </p>

              <div className="bf-circuit-line mb-7 max-w-xl" aria-hidden="true" />

              <div className="space-y-3.5 mb-8">
                {[
                  { icon: Anchor, title: 'Asset Earnings', desc: 'Revenue, rev/hour and seats filled — per boat.' },
                  { icon: TrendingUp, title: 'Revenue Mix', desc: 'What actually sells, realized prices and add-on attach.' },
                  { icon: Calendar, title: 'Demand Center', desc: 'Peak days, start times and how far ahead customers book.' },
                  { icon: Users, title: 'Customer Insights', desc: 'Repeat rates, group sizes and your highest-value guests.' },
                ].map((f) => (
                  <div key={f.title} className="flex items-start gap-3.5">
                    <span className="w-9 h-9 rounded-full bg-[#EBF2FF] text-[#2563EB] flex items-center justify-center shrink-0">
                      <f.icon className="w-4 h-4" strokeWidth={1.75} />
                    </span>
                    <div>
                      <span className="font-semibold text-gray-900 text-sm">{f.title}</span>
                      <span className="text-gray-500 text-sm"> — {f.desc}</span>
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex items-start gap-3 rounded-2xl bg-white ring-1 ring-slate-900/[0.05] shadow-sm p-4 mb-8 max-w-xl">
                <Sparkles className="w-4 h-4 text-[#2563EB] mt-0.5 shrink-0" />
                <p className="text-sm text-gray-600 leading-relaxed">
                  <span className="font-semibold text-gray-900">AI Suggestions built in.</span>{' '}
                  HELM spots the gaps and hands you one clear move — read-only, from your data only.
                </p>
              </div>

              <div className="flex flex-wrap items-center gap-4">
                <button
                  onClick={() => { trackSignupStarted('supplier_helm_section'); navigate('/signup'); }}
                  className="group inline-flex items-center gap-2 text-sm font-semibold text-[#2563EB] hover:text-[#1D4ED8] transition-colors"
                  data-testid="helm-section-cta"
                >
                  Start free — HELM included
                  <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                </button>
                <span className="text-xs text-gray-400 font-medium">Your data only · No setup · Every plan</span>
              </div>
            </div>

            {/* Right — HELM Intelligence render */}
            <div className="relative" aria-hidden="true">
              <img
                src="/images/bf-helm.webp"
                alt="HELM Intelligence dashboard — revenue overview, fleet earnings, funnel and customer insights with the mobile companion"
                loading="lazy"
                data-testid="helm-section-visual"
                className="w-full block"
              />
            </div>
          </div>
        </div>
      </section>

      {/* BookingFlows Beacon + Beacon Tender — the pixel that powers HELM's
          acquisition reports and feeds retargeting Campaigns. */}
      <section className="py-24 bg-white" data-testid="supplier-beacon-section">
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Copy + snippet + the loop — right side on desktop */}
            <div className="min-w-0 lg:order-2">
              <div className="inline-flex items-center gap-2.5 px-3.5 py-1.5 rounded-full bg-emerald-50 border border-emerald-500/20 mb-6">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
                </span>
                <span className="text-[11px] font-bold uppercase tracking-[0.14em] text-emerald-700">BookingFlows Beacon · Your own pixel</span>
              </div>

              <h2 className="text-3xl sm:text-4xl lg:text-[2.75rem] font-bold tracking-tight leading-[1.08] text-gray-900 mb-5">
                Your own pixel.<br />Wired straight into your bookings.
              </h2>
              <p className="text-base sm:text-lg text-gray-600 leading-relaxed mb-7 max-w-xl">
                Think Google Analytics or Meta Pixel — except it's yours, wired
                straight into your bookings. One line of code on your website and you
                know exactly which marketing channels produce bookings — not just clicks.
              </p>

              {/* The loop: visitors → audiences → bookings */}
              <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="beacon-loop-strip">
                {[
                  { icon: BeaconIcon, label: 'Understand your visitors' },
                  { icon: BarChart3, label: 'Build audiences' },
                  { icon: Megaphone, label: 'Win more bookings' },
                ].map((s, i) => (
                  <React.Fragment key={s.label}>
                    {i > 0 && <ArrowRight className="w-3.5 h-3.5 text-gray-300 shrink-0" />}
                    <span className="inline-flex items-center gap-2 px-3.5 py-2 rounded-full bg-[#EBF2FF] text-[#2563EB] text-xs font-bold">
                      <s.icon className="w-3.5 h-3.5" strokeWidth={2} />
                      {s.label}
                    </span>
                  </React.Fragment>
                ))}
              </div>
              <p className="text-sm text-gray-500 leading-relaxed max-w-xl">
                That's the whole loop — see where visitors come from, turn them into
                audiences, and bring them back for a few dollars a day. No agencies,
                no ad platforms, no exporting data.
              </p>
            </div>

            {/* Beacon render — left side on desktop */}
            <div className="relative min-w-0 w-full lg:order-1">
              <img
                src="/images/bf-beacon.webp"
                alt="BookingFlows Beacon — live visitor events streaming from your website into HELM"
                loading="lazy"
                data-testid="beacon-section-visual"
                className="w-full block"
              />
              <a href="https://chromewebstore.google.com/detail/bookingflows-beacon-tende/lomkgfganpkdkeeeipjdngkcmhbbahkh"
                target="_blank" rel="noopener noreferrer" data-testid="beacon-tender-store-link"
                className="group mt-2 inline-flex items-center gap-2 text-sm font-semibold text-emerald-700 hover:text-emerald-800 transition-colors">
                <BeaconIcon className="w-4 h-4" />
                Beacon Tender — Add to Chrome
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Kiosk Direct — Walk-ins 2.0: dockside tablet → paid booking + signed waiver */}
      <KioskDirectSection onCta={() => { trackSignupStarted('supplier_kiosk_direct_section'); navigate('/signup'); }} />





      {/* 💳 BookingFlows Finance × Pliant — company cards partnership.
          HIDDEN ON PRODUCTION until the Pliant partnership is official.
          Card readers (Stripe M2) pending hardware verification. */}
      {process.env.NODE_ENV !== 'production' && (
      <section className="py-24 bg-white" data-testid="supplier-pliant-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
              {/* Left — Pliant card + dashboard + wallet visual */}
              <div className="relative" aria-hidden="true">
                <img
                  src="/images/pliant-suite.webp"
                  alt="Pliant company card with the Pliant dashboard and mobile wallet"
                  className="w-full block"
                  loading="lazy"
                  data-testid="pliant-suite-visual"
                />
              </div>

              {/* Right — copy */}
              <div>
                <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white ring-1 ring-slate-900/[0.08] mb-6">
                  <span className="w-1.5 h-1.5 rounded-full bg-lime-500" />
                  <span className="text-[11px] font-bold uppercase tracking-[0.14em] text-gray-900">BookingFlows Finance &times; Pliant</span>
                  <span className="text-[10px] font-bold uppercase tracking-[0.12em] text-lime-700 bg-lime-50 border border-lime-200 rounded-full px-2 py-0.5">Coming soon*</span>
                </div>

                <h2 className="text-3xl sm:text-4xl lg:text-[2.75rem] font-bold tracking-tight leading-[1.08] text-gray-900 mb-5">
                  Company cards that file their own expenses.
                </h2>
                <p className="text-base sm:text-lg text-gray-600 leading-relaxed mb-8 max-w-xl">
                  We&apos;re partnering with <span className="font-semibold text-gray-900">Pliant</span> —
                  a dedicated corporate-card platform — to put virtual and physical cards in your
                  crew&apos;s hands. Every swipe lands in your BookingFlows expenses automatically,
                  and flows straight on to QuickBooks from there.
                </p>

                <div className="space-y-3.5 mb-8">
                  {[
                    { title: 'Virtual + physical cards for the whole crew', desc: 'issue a card per captain, per boat, or per job in seconds.' },
                    { title: 'Cashback on spend', desc: 'the fuel run pays you back a little every time.' },
                    { title: 'Spend controls per card', desc: 'budgets and limits you set — your crew can only spend what you allow.' },
                    { title: 'Expenses file themselves', desc: 'transactions sync into BookingFlows automatically via Pliant\u2019s APIs — no receipts to chase.' },
                    { title: 'Plays perfectly with QuickBooks', desc: 'card spend rides the same sync your books already run on.' },
                  ].map((f) => (
                    <div key={f.title} className="flex items-start gap-3.5">
                      <span className="w-6 h-6 rounded-full bg-gray-900 text-white flex items-center justify-center shrink-0 mt-0.5">
                        <Check className="w-3.5 h-3.5" strokeWidth={2.5} />
                      </span>
                      <div>
                        <span className="font-semibold text-gray-900 text-sm">{f.title}</span>
                        <span className="text-gray-500 text-sm"> — {f.desc}</span>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="rounded-2xl bg-gray-50 ring-1 ring-slate-900/[0.05] p-4 mb-8 max-w-xl" data-testid="pliant-trust-note">
                  <p className="text-sm text-gray-600 leading-relaxed">
                    <span className="inline-flex items-center gap-1.5 font-semibold text-gray-900"><Shield className="w-4 h-4 text-lime-600" />Built on serious rails.</span>{' '}
                    Pliant is PCI DSS certified, ISO/IEC 27001 certified, and operates under an
                    e-money licence. Card readers for dockside payments are in the works too.
                  </p>
                </div>

                <div className="flex flex-wrap items-center gap-4">
                  <button
                    onClick={() => navigate('/demo?src=finance&topic=pliant')}
                    className="group inline-flex items-center gap-2 text-sm font-semibold text-gray-900 hover:text-black transition-colors"
                    data-testid="pliant-section-cta"
                  >
                    Get on the waitlist
                    <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                  </button>
                  <span className="text-xs text-gray-400 font-medium" data-testid="pliant-footnote">
                    *Partnership in progress — details subject to change before launch.
                  </span>
                </div>
              </div>
            </div>

            {/* Full-width card lineup */}
            <div className="mt-14">
              <img
                src="/images/pliant-cards.webp"
                alt="The Pliant card lineup — virtual and physical company cards in every color"
                className="w-full block rounded-2xl"
                loading="lazy"
                data-testid="pliant-cards-visual"
              />
            </div>
        </div>
      </section>
      )}

      {/* Data migration — switching-anxiety killer. Sits right after pricing
          where "but I already have bookings in another system" objections peak. */}
      <section className="py-24 bg-white animate-on-scroll" data-testid="supplier-migration-section">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-[1.1fr_1fr] gap-12 lg:gap-16 items-center">
            {/* Left: Copy */}
            <div>
              <div className="inline-flex items-center gap-2 text-blue-600 text-xs font-semibold uppercase tracking-[0.2em] mb-5">
                <ArrowLeftRight className="w-3.5 h-3.5" />
                Switching Is Easy
              </div>
              <h3 className="text-4xl md:text-5xl font-bold tracking-tight text-gray-900 leading-[1.1] mb-5">
                Already have trips booked?{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">
                  We'll migrate everything.
                </span>
              </h3>
              <p className="text-base sm:text-lg text-gray-600 leading-relaxed mb-7">
                Existing bookings, customers, schedules, gift cards — our engineers move it
                all for you. Data migration is a standard, well-worn software process for our
                team, not a project you have to manage. You keep taking bookings the entire time.
              </p>
              <div className="space-y-3">
                {[
                  'Upcoming trips & past booking history come with you',
                  'Customers, waivers & gift card balances preserved',
                  'Handled end-to-end by our engineers — typically days, not weeks',
                  'Zero downtime — keep selling while we move you over',
                ].map((point, i) => (
                  <div key={i} className="flex items-start gap-2.5">
                    <Check className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" />
                    <span className="text-sm sm:text-base text-gray-700">{point}</span>
                  </div>
                ))}
              </div>
              <button
                onClick={() => navigate('/onboarding')}
                data-testid="migration-onboarding-link"
                className="inline-flex items-center gap-1.5 mt-7 text-sm font-medium text-blue-600 hover:text-blue-700 transition-colors"
              >
                See the full onboarding guide <ArrowRight className="w-4 h-4" />
              </button>
            </div>

            {/* Right: 3-step migration card */}
            <div>
              <div className="bg-gradient-to-br from-gray-50 to-blue-50/40 rounded-2xl border border-gray-100 p-6 sm:p-8 shadow-sm" data-testid="supplier-migration-steps">
                {/* Animated transfer strip — old system → BookingFlows */}
                <div className="flex items-center gap-3 mb-7 rounded-xl bg-white ring-1 ring-slate-900/[0.05] px-4 py-3.5" aria-hidden="true">
                  <div className="flex items-center gap-2.5 shrink-0">
                    <span className="w-9 h-9 rounded-xl bg-slate-100 text-slate-500 flex items-center justify-center">
                      <Database className="w-4 h-4" />
                    </span>
                    <div>
                      <div className="text-[11px] font-bold text-slate-700 leading-tight">Old system</div>
                      <div className="text-[10px] text-slate-400">any export</div>
                    </div>
                  </div>
                  <div className="relative flex-1 h-px bg-gradient-to-r from-slate-200 via-blue-300 to-blue-500 mx-1">
                    <span className="bf-migrate-dot" />
                    <span className="bf-migrate-dot" style={{ animationDelay: '0.8s' }} />
                    <span className="bf-migrate-dot" style={{ animationDelay: '1.6s' }} />
                    <span className="absolute left-1/2 -translate-x-1/2 -top-[13px] w-7 h-7 rounded-full bg-blue-600 text-white flex items-center justify-center shadow-lg shadow-blue-500/40">
                      <Zap className="w-3.5 h-3.5" fill="currentColor" />
                    </span>
                  </div>
                  <div className="flex items-center gap-2.5 shrink-0">
                    <div className="text-right">
                      <div className="text-[11px] font-bold text-blue-700 leading-tight">BookingFlows</div>
                      <div className="text-[10px] text-slate-400">zero downtime</div>
                    </div>
                    <span className="w-9 h-9 rounded-xl bg-[#EBF2FF] flex items-center justify-center">
                      <img src="/logo-icon.png" className="w-5 h-5 rounded" alt="" />
                    </span>
                  </div>
                </div>

                <p className="text-[11px] uppercase tracking-[0.18em] text-gray-400 font-semibold mb-5 flex items-center gap-2">
                  <Database className="w-3.5 h-3.5" /> How Migration Works
                </p>
                <div className="space-y-5">
                  {[
                    { step: '1', title: 'Share an export', desc: 'A spreadsheet or export from your current system — any format works.' },
                    { step: '2', title: 'Our engineers map & import', desc: 'Trips, customers, schedules and balances land exactly where they belong.' },
                    { step: '3', title: 'Verify together & go live', desc: 'We walk through it with you side by side. Flip the switch when it all looks right.' },
                  ].map((s, i) => (
                    <div key={i} className="flex items-start gap-4">
                      <div className="w-8 h-8 rounded-full bg-blue-600 text-white text-sm font-bold flex items-center justify-center flex-shrink-0 shadow-sm">
                        {s.step}
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-gray-900 mb-0.5">{s.title}</p>
                        <p className="text-sm text-gray-600 leading-relaxed">{s.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-6 pt-5 border-t border-gray-200/70 flex items-center gap-2.5">
                  <Shield className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                  <p className="text-xs text-gray-500">Migration is included — no extra fees, no "implementation packages."</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 🔒 Data trust — answers the "are you going to steal my customers?"
          objection head-on. Sits right after migration (where operators hand
          over their data) — the dark Enterprise-grade Security section lower
          down stays as-is. */}
      <section className="py-24 bg-white" data-testid="supplier-data-trust-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center justify-center gap-2 text-blue-600 text-xs font-semibold uppercase tracking-[0.2em] mb-6">
            <Lock className="w-3.5 h-3.5" />
            Your data · Siloed &amp; secure
          </div>
          <img
            src="/images/bf-data-security.webp"
            alt="Your data is safe, always — encrypted at rest and in transit, access control, secure infrastructure and compliance-ready standards"
            loading="lazy"
            data-testid="data-trust-section-visual"
            className="w-full block max-w-3xl mx-auto mb-14"
          />
          <h3 className="text-4xl md:text-5xl font-bold tracking-tight text-gray-900 leading-[1.12] mb-8">
            Your data is yours.{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">Full stop.</span>
          </h3>
          <p className="text-lg sm:text-xl text-gray-600 leading-relaxed max-w-3xl mx-auto mb-10">
            Every company on BookingFlows runs in its own silo — your customers, bookings and
            revenue are visible to you and no one else. Not to other operators, never sold,
            never shared, never used to compete with you. We&apos;re a software company: our only
            business is your subscription, not your customer list.
          </p>
          <div className="flex flex-wrap justify-center gap-x-8 gap-y-3 mb-10">
            {[
              'Siloed per company',
              'AES-256 at rest · TLS in transit',
              'RBAC, 2FA & FIDO2 security keys',
              'Export or delete anytime',
            ].map((t) => (
              <div key={t} className="flex items-center gap-2">
                <Check className="w-5 h-5 text-blue-600 flex-shrink-0" />
                <span className="text-gray-700 text-sm sm:text-base">{t}</span>
              </div>
            ))}
          </div>
          <button
            onClick={() => navigate('/security')}
            className="group inline-flex items-center gap-2 text-sm font-semibold text-[#2563EB] hover:text-[#1D4ED8] transition-colors"
            data-testid="data-trust-section-cta"
          >
            Read how we protect your data
            <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
          </button>
        </div>
      </section>

      {/* Dedicated Support — Help Center + advanced ticketing + Bosun AI */}
      <section className="py-24 bg-white relative overflow-hidden" data-testid="supplier-support-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Copy — right side on desktop (visual flipped to the left) */}
            <div className="lg:order-2">
              <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[#EBF2FF] border border-[#2563EB]/15 mb-6">
                <LifeBuoy className="w-3.5 h-3.5 text-[#2563EB]" strokeWidth={2.25} />
                <span className="text-[11px] font-bold uppercase tracking-[0.14em] text-[#2563EB]">Support Center · A real team behind you</span>
              </div>
              <h3 className="text-4xl md:text-5xl font-bold tracking-tight text-gray-900 leading-[1.1] mb-5">
                Advanced support.{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">
                  Not a black hole.
                </span>
              </h3>
              <p className="text-base sm:text-lg text-gray-600 leading-relaxed mb-7">
                Bosun answers the quick questions in seconds — and for everything else, your
                company gets a dedicated Support Center: searchable guides for every feature,
                plus an enterprise ticketing system that emails you at every step, from
                "we've got it" to "it's resolved."
              </p>
              <div className="space-y-3 mb-8">
                {[
                  'A full Help Center — guides for bookings, waivers, payments, kiosk & more',
                  'Tracked support tickets with email updates at every status change',
                  'Real engineers on the other end — the people who built the platform',
                ].map((line, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <div className="w-5 h-5 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="w-3 h-3" strokeWidth={3} />
                    </div>
                    <span className="text-sm sm:text-base text-gray-700">{line}</span>
                  </div>
                ))}
              </div>
              <button
                onClick={() => navigate('/support')}
                className="group inline-flex items-center gap-2 text-sm font-semibold text-[#2563EB] hover:text-[#1D4ED8] transition-colors"
                data-testid="supplier-support-cta"
              >
                Visit the Support Center
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </button>
            </div>

            {/* Support Center mock — left side on desktop */}
            <div className="relative max-w-md mx-auto lg:mx-0 lg:mr-auto w-full lg:order-1" aria-hidden="true">
              <div className="absolute -top-5 left-6 z-10 inline-flex items-center gap-2 px-3.5 py-2 rounded-full bg-white shadow-lg ring-1 ring-slate-900/[0.06]">
                <span className="w-5 h-5 rounded-md bg-[#2563EB] text-white flex items-center justify-center">
                  <LifeBuoy className="w-3 h-3" />
                </span>
                <span className="text-xs font-bold text-slate-900">Support <span className="italic">Center</span> <span className="font-normal text-slate-400">by BookingFlows</span></span>
              </div>
              <div className="rounded-[1.5rem] bg-white ring-1 ring-slate-900/[0.06] shadow-[0_40px_80px_-30px_rgba(37,99,235,0.3)] p-6 pt-8">
                {/* Ticket card */}
                <div className="rounded-2xl ring-1 ring-slate-900/[0.05] p-4 mb-4">
                  <div className="flex items-center justify-between gap-2 mb-1.5">
                    <span className="font-mono text-[11px] text-[#2563EB]/70 font-bold">BFSUP-4F2A1C</span>
                    <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 border border-emerald-200 px-2 py-0.5 text-[10px] font-bold text-emerald-700">
                      <Check className="w-2.5 h-2.5" strokeWidth={3} /> Resolved
                    </span>
                  </div>
                  <div className="text-sm font-bold text-slate-900 mb-3">Refund not showing on booking #8841</div>
                  {/* Status timeline */}
                  <div className="space-y-2.5">
                    {[
                      { label: 'Ticket received — confirmation emailed', t: '9:02 AM' },
                      { label: 'Team reviewing your ticket', t: '9:14 AM' },
                      { label: 'Resolved — refund reissued via Stripe', t: '10:47 AM' },
                    ].map((s, i) => (
                      <div key={s.label} className="flex items-center gap-2.5">
                        <span className={`w-4 h-4 rounded-full flex items-center justify-center shrink-0 ${i === 2 ? 'bg-emerald-500 text-white' : 'bg-[#EBF2FF] text-[#2563EB]'}`}>
                          <Check className="w-2.5 h-2.5" strokeWidth={3.5} />
                        </span>
                        <span className="text-[12px] font-medium text-slate-600 flex-1">{s.label}</span>
                        <span className="text-[10px] text-slate-400">{s.t}</span>
                      </div>
                    ))}
                  </div>
                </div>
                {/* Help center strip */}
                <div className="flex items-center gap-2.5 rounded-xl bg-slate-50 px-3.5 py-3">
                  <Mail className="w-4 h-4 text-[#2563EB] shrink-0" />
                  <span className="text-[12px] font-medium text-slate-600">Email updates at every step — nothing disappears into a queue.</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ───────────────────────────────────────────────────────────────
          Zapier — every confirmed booking can trigger a Zap in 8,000+ apps.
          Truthful: live feature (Marketing → Integrations), fires after
          Stripe confirms payment, no code required.
      ─────────────────────────────────────────────────────────────── */}
      <section className="py-24 relative overflow-hidden bg-gradient-to-b from-white via-orange-50/40 to-white" data-testid="supplier-zapier-section">
        <div className="pointer-events-none absolute top-16 right-[10%] h-72 w-72 rounded-full bg-orange-100/50 blur-3xl" aria-hidden="true" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Copy */}
            <div className="animate-on-scroll">
              <div className="inline-flex items-center gap-2 text-[#FF4F00] text-xs font-semibold uppercase tracking-[0.2em] mb-4">
                <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <path d="M14.13 12.01c0 .62-.11 1.22-.32 1.77-.55.21-1.15.33-1.78.33h-.01c-.63 0-1.23-.12-1.78-.33-.21-.55-.33-1.15-.33-1.77v-.01c0-.63.12-1.23.33-1.78.55-.21 1.15-.33 1.78-.33h.01c.63 0 1.23.12 1.78.33.21.55.32 1.15.32 1.78v.01zm7.6-1.35h-5.71l4.04-4.04a10.6 10.6 0 0 0-1.07-1.26 10.6 10.6 0 0 0-1.26-1.07l-4.04 4.04V2.62c-.54-.09-1.1-.14-1.66-.14h-.01c-.57 0-1.12.05-1.67.14v5.71L6.31 4.29c-.45.32-.87.68-1.26 1.07-.39.39-.75.81-1.07 1.26l4.04 4.04H2.31s-.14 1.1-.14 1.67v.01c0 .57.05 1.12.14 1.66h5.71l-4.04 4.04c.64.9 1.43 1.69 2.33 2.33l4.04-4.04v5.71c.54.09 1.1.14 1.66.14h.02c.56 0 1.12-.05 1.66-.14v-5.71l4.04 4.04c.45-.32.87-.68 1.26-1.07.39-.39.75-.81 1.07-1.26l-4.04-4.04h5.71c.09-.54.14-1.1.14-1.66v-.02c0-.56-.05-1.12-.14-1.66z" />
                </svg>
                Zapier integration
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 tracking-tight">
                Every booking, everywhere.
                <span className="block text-gray-400">Zapier connects you to 8,000+ apps.</span>
              </h2>
              <p className="mt-5 text-lg text-gray-600 leading-relaxed">
                The moment a checkout is paid, BookingFlows can fire the event into any Zap —
                a new row in Google Sheets, a Slack ping to the crew channel, a Mailchimp
                contact, a Xero invoice. New leads too.
              </p>
              <ul className="mt-6 space-y-3">
                {[
                  'Fires only after Stripe confirms the payment — never an unpaid checkout',
                  'No code: paste one Zapier webhook URL and you\u2019re live',
                  'Send a test event with one click to map your fields',
                ].map((t) => (
                  <li key={t} className="flex items-start gap-3">
                    <svg className="w-5 h-5 text-[#FF4F00] shrink-0 mt-0.5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                      <path fillRule="evenodd" d="M16.7 5.3a1 1 0 010 1.4l-7.5 7.5a1 1 0 01-1.4 0l-3.5-3.5a1 1 0 111.4-1.4l2.8 2.79 6.8-6.79a1 1 0 011.4 0z" clipRule="evenodd" />
                    </svg>
                    <span className="text-gray-700">{t}</span>
                  </li>
                ))}
              </ul>
              <button
                onClick={() => { trackSignupStarted('supplier_zapier_section'); navigate('/signup'); }}
                data-testid="supplier-zapier-signup"
                className="group mt-8 inline-flex items-center gap-2 text-sm font-semibold text-[#FF4F00] hover:text-[#E64700] transition-colors">
                Automate your bookings
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </button>
            </div>

            {/* Zap-flow mock */}
            <div className="animate-on-scroll">
              <div className="bg-white rounded-2xl border border-gray-200 shadow-[0_25px_60px_-25px_rgba(15,23,42,0.25)] p-6 sm:p-8" data-testid="supplier-zapier-mock">
                <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wide text-gray-400 mb-5">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                  Live event stream
                </div>
                {/* Event card */}
                <div className="rounded-xl border border-blue-100 bg-blue-50/50 p-4">
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <img src="/logo-icon.png" alt="BookingFlows" className="h-8 w-8 rounded-lg shrink-0" />
                      <div className="min-w-0">
                        <div className="text-sm font-semibold text-gray-900 truncate">booking.confirmed</div>
                        <div className="text-xs text-gray-500 truncate">Sunset Cruise · 4 guests · $420 paid</div>
                      </div>
                    </div>
                    <span className="text-[10px] font-bold text-emerald-700 bg-emerald-50 ring-1 ring-emerald-200 rounded-full px-2 py-0.5 shrink-0">PAID</span>
                  </div>
                </div>
                {/* Connector */}
                <div className="flex flex-col items-center py-1.5">
                  <div className="h-4 w-px bg-gray-200" />
                  <svg className="w-6 h-6 text-[#FF4F00]" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                    <path d="M14.13 12.01c0 .62-.11 1.22-.32 1.77-.55.21-1.15.33-1.78.33h-.01c-.63 0-1.23-.12-1.78-.33-.21-.55-.33-1.15-.33-1.77v-.01c0-.63.12-1.23.33-1.78.55-.21 1.15-.33 1.78-.33h.01c.63 0 1.23.12 1.78.33.21.55.32 1.15.32 1.78v.01zm7.6-1.35h-5.71l4.04-4.04a10.6 10.6 0 0 0-1.07-1.26 10.6 10.6 0 0 0-1.26-1.07l-4.04 4.04V2.62c-.54-.09-1.1-.14-1.66-.14h-.01c-.57 0-1.12.05-1.67.14v5.71L6.31 4.29c-.45.32-.87.68-1.26 1.07-.39.39-.75.81-1.07 1.26l4.04 4.04H2.31s-.14 1.1-.14 1.67v.01c0 .57.05 1.12.14 1.66h5.71l-4.04 4.04c.64.9 1.43 1.69 2.33 2.33l4.04-4.04v5.71c.54.09 1.1.14 1.66.14h.02c.56 0 1.12-.05 1.66-.14v-5.71l4.04 4.04c.45-.32.87-.68 1.26-1.07.39-.39.75-.81 1.07-1.26l-4.04-4.04h5.71c.09-.54.14-1.1.14-1.66v-.02c0-.56-.05-1.12-.14-1.66z" />
                  </svg>
                  <div className="h-4 w-px bg-gray-200" />
                </div>
                {/* Destination chips */}
                <div className="grid grid-cols-2 gap-2.5">
                  {[
                    ['Google Sheets', 'New row added', '#0F9D58'],
                    ['Slack', '#bookings pinged', '#611F69'],
                    ['Mailchimp', 'Contact created', '#FFE01B'],
                    ['Xero', 'Invoice raised', '#13B5EA'],
                  ].map(([app, action, color]) => (
                    <div key={app} className="rounded-xl border border-gray-100 bg-gray-50/70 px-3.5 py-3 flex items-center gap-2.5">
                      <span className="h-2 w-2 rounded-full shrink-0" style={{ backgroundColor: color }} />
                      <div className="min-w-0">
                        <div className="text-xs font-semibold text-gray-800 truncate">{app}</div>
                        <div className="text-[11px] text-gray-400 truncate">{action}</div>
                      </div>
                      <svg className="w-3.5 h-3.5 text-emerald-500 ml-auto shrink-0" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                        <path fillRule="evenodd" d="M16.7 5.3a1 1 0 010 1.4l-7.5 7.5a1 1 0 01-1.4 0l-3.5-3.5a1 1 0 111.4-1.4l2.8 2.79 6.8-6.79a1 1 0 011.4 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                  ))}
                </div>
                <p className="mt-5 text-[11px] text-gray-400 text-center">
                  and 8,000+ more apps via Zapier — set up in minutes, no code
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* API — for operators with a tech team (and the devs evaluating for them).
          MOVED up under the Zapier section per founder — visual LEFT, copy right. */}
      <section className="py-24 bg-white" data-testid="supplier-api-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div className="relative">
              <img
                src="/images/bf-api.webp"
                alt="BookingFlows developer API — clean JSON responses for bookings, services, assets, team and waivers"
                loading="lazy"
                data-testid="api-section-visual"
                className="w-full block"
              />
            </div>
            <div>
              <div className="inline-flex items-center gap-2 text-blue-600 text-xs font-semibold uppercase tracking-[0.2em] mb-4">
                <Code className="w-3.5 h-3.5" />
                Developer API · Included on every plan
              </div>
              <h3 className="text-4xl md:text-5xl font-bold tracking-tight text-gray-900 leading-[1.1] mb-4">
                Your data,{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">on tap.</span>
              </h3>
              <p className="text-lg text-gray-500 leading-relaxed mb-8 max-w-xl">
                A clean, read-only REST API for bookings, services, assets, team and signed waivers.
                Most operators never touch it — but your accountant, agency or custom dashboard will love that it's there.
              </p>
              <div className="space-y-4 mb-8">
                {[
                  ['Keys you control', 'Create keys in Settings, grant each one only the permissions the integration needs, revoke instantly.'],
                  ['Privacy-first JSON', 'Responses stay within your own operational records — internal and compliance data never leaves the platform.'],
                  ['Secure, scoped API access', 'Header-only authentication, keys shown once and securely stored, every request scoped to your organization.'],
                ].map(([title, desc]) => (
                  <div key={title} className="flex items-start gap-3">
                    <span className="w-5 h-5 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="w-3 h-3" strokeWidth={3} />
                    </span>
                    <p className="text-sm text-gray-600 leading-relaxed">
                      <span className="font-semibold text-gray-900">{title}.</span> {desc}
                    </p>
                  </div>
                ))}
              </div>
              <button
                onClick={() => navigate('/developers')}
                data-testid="landing-api-docs-link"
                className="group inline-flex items-center gap-2 text-sm font-semibold text-[#2563EB] hover:text-[#1D4ED8] transition-colors"
              >
                View the API docs
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Professional Reports — trips & money reports compiled as clean CSVs.
          Visual RIGHT (API section above is visual-left) to keep the rhythm. */}
      <section className="py-24 bg-white" data-testid="supplier-reports-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div>
              <div className="inline-flex items-center gap-2 text-blue-600 text-xs font-semibold uppercase tracking-[0.2em] mb-4">
                <FileSpreadsheet className="w-3.5 h-3.5" />
                Professional Reporting · Every plan
              </div>
              <h3 className="text-4xl md:text-5xl font-bold tracking-tight text-gray-900 leading-[1.1] mb-4">
                Reports your accountant{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">will love.</span>
              </h3>
              <p className="text-lg text-gray-500 leading-relaxed mb-8 max-w-xl">
                Daily, weekly, monthly — or any range you pick. Professional trips &
                money reports, compiled for you and exported as clean <span className="font-semibold text-gray-700">.csv</span> files.
                No copy-pasting into spreadsheets, ever.
              </p>
              <div className="space-y-4 mb-8">
                {[
                  ['Trips & money in one report', 'Pick a range, filter by status — collected, owed, add-ons and refunds are totaled for you.'],
                  ['Revenue, expense & asset breakdowns', 'See what you earned, what you spent, and how each asset performed.'],
                  ['A clear P&L', 'Understand your profitability at a glance — always current.'],
                  ['Custom & exportable', 'Filter, customize and export any report as a clean .csv — ready for your accountant or your books.'],
                ].map(([title, desc]) => (
                  <div key={title} className="flex items-start gap-3">
                    <span className="w-5 h-5 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="w-3 h-3" strokeWidth={3} />
                    </span>
                    <p className="text-sm text-gray-600 leading-relaxed">
                      <span className="font-semibold text-gray-900">{title}.</span> {desc}
                    </p>
                  </div>
                ))}
              </div>
              <button
                onClick={() => { trackSignupStarted('supplier_reports_section'); navigate('/signup'); }}
                data-testid="reports-section-cta"
                className="group inline-flex items-center gap-2 text-sm font-semibold text-[#2563EB] hover:text-[#1D4ED8] transition-colors"
              >
                Start free — reports included
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </button>
            </div>
            <div className="relative">
              <img
                src="/images/bf-reports.webp"
                alt="BookingFlows Reports — trips and money reports with revenue, expense, asset and P&L breakdowns, exportable as CSV"
                loading="lazy"
                data-testid="reports-section-visual"
                className="w-full block"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Concrete feature grid — replaces abstract "does more" cards with
          specific, sales-ready features operators actually compare on. */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16 max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-2 text-blue-600 text-xs font-semibold uppercase tracking-[0.2em] mb-4">
              <Sparkles className="w-3.5 h-3.5" />
              Everything operators need
            </div>
            <h3 className="text-4xl md:text-5xl font-bold tracking-tight text-gray-900 leading-[1.1] mb-4">
              The features your competitors{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">charge extra for</span>
            </h3>
            <p className="text-lg text-gray-500 leading-relaxed">
              All included. No upsells. No "Pro plan" upgrade prompts.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              // ── Revenue tools ────────────────────────────────────────────
              {
                icon: <BadgeDollarSign className="w-6 h-6" />,
                title: 'Gift Cards',
                desc: 'Depleting-balance gift cards with partial Stripe charges. Buyers and recipients get branded emails. Sell year-round, redeem instantly.',
                tint: 'from-violet-500 to-purple-600',
              },
              {
                icon: <TrendingUp className="w-6 h-6" />,
                title: 'Dynamic Pricing',
                desc: 'Surge weekends, discount slow days, tier by season — automatically. Pull more revenue from the same calendar.',
                tint: 'from-blue-500 to-cyan-600',
              },
              {
                icon: <Percent className="w-6 h-6" />,
                title: 'Discount Codes',
                desc: 'Reusable percentage or fixed-amount codes. Cap usage, set expiry, target specific services. Track redemption in real time.',
                tint: 'from-rose-500 to-pink-600',
              },
              // ── Financial control ────────────────────────────────────────
              {
                icon: <SlidersHorizontal className="w-6 h-6" />,
                title: 'Flexible Fee Control',
                desc: 'You decide who pays the fees. Operator absorbs, customer absorbs, or split. Add your own service fee to every booking with a toggle.',
                tint: 'from-emerald-500 to-teal-600',
                badge: 'New',
              },
              {
                icon: <Globe className="w-6 h-6" />,
                title: 'Multi-Currency',
                desc: 'USD, AED, EUR, GBP & more. Operators in the US, Med, UAE and beyond — each in their own currency and timezone.',
                tint: 'from-amber-500 to-orange-600',
              },
              {
                icon: <BarChart3 className="w-6 h-6" />,
                title: 'Per-Tenant Analytics',
                desc: 'GA4, Google Ads & Meta Pixel events fire under your own tenant IDs. Scale ad spend with confidence, not guesswork.',
                tint: 'from-cyan-500 to-sky-600',
              },
              // ── Operations ───────────────────────────────────────────────
              {
                icon: <Users className="w-6 h-6" />,
                title: 'Assignment on Confirmation',
                desc: 'Captains and guides claim trips from a shared dispatch queue. No more group-chat assignment chaos.',
                tint: 'from-indigo-500 to-blue-600',
              },
              {
                icon: <Zap className="w-6 h-6" />,
                title: 'No-Code Service Editor',
                desc: 'Spin up a new tour, edit pricing, or change a capacity in under a minute. Drag, drop, save — live for customers.',
                tint: 'from-pink-500 to-rose-600',
              },
              {
                icon: <Download className="w-6 h-6" />,
                title: 'Native iOS & Android Apps',
                desc: 'Your team books, dispatches and checks guests in from real native apps — not a wrapped browser tab.',
                tint: 'from-blue-600 to-indigo-700',
              },
              // ── Trust & growth ───────────────────────────────────────────
              {
                icon: <FileText className="w-6 h-6" />,
                title: 'Digital Waivers',
                desc: 'PIN-protected, device-fingerprinted, multi-signer ready. Legal audit trail captured automatically.',
                tint: 'from-slate-600 to-gray-700',
              },
              {
                icon: <Mail className="w-6 h-6" />,
                title: 'Automated Customer Comms',
                desc: 'SMS reminders, abandoned-cart recovery, thank-you emails, review nudges — sent on your behalf.',
                tint: 'from-sky-500 to-blue-600',
              },
              {
                icon: <Code className="w-6 h-6" />,
                title: 'Custom Websites Built For You',
                desc: 'Need more than a widget? We build a competitive, SEO-primed website tailored to your business — no templates.',
                tint: 'from-fuchsia-500 to-pink-600',
                badge: 'New',
              },
            ].map((f, i) => (
              <div
                key={i}
                className="group relative rounded-2xl border border-gray-100 bg-white p-6 hover:border-gray-200 hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200"
              >
                {f.badge && (
                  <span className="absolute top-4 right-4 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">
                    {f.badge}
                  </span>
                )}
                <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${f.tint} text-white flex items-center justify-center mb-4 shadow-sm`}>
                  {f.icon}
                </div>
                <h4 className="text-base font-semibold text-gray-900 mb-1.5 tracking-tight">{f.title}</h4>
                <p className="text-sm text-gray-500 leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>

          {/* Honest closer — operator-to-operator nod */}
          <div className="text-center mt-12">
            <p className="text-sm text-gray-400 italic">
              ...and a few hundred details we'll never put on a marketing page.
            </p>
          </div>
        </div>
      </section>



      {/* Ask Bosun — AI first mate. Character + instant help, guardrails up front. */}
      <section className="py-24 bg-white" data-testid="supplier-bosun-section">
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[#1F2A5C]/5 border border-[#1F2A5C]/15 mb-6">
              <Anchor className="w-3.5 h-3.5 text-[#1F2A5C]" strokeWidth={2.25} />
              <span className="text-[11px] font-bold uppercase tracking-[0.14em] text-[#1F2A5C]">Meet Bosun · Included free</span>
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-[2.75rem] font-bold tracking-tight leading-[1.08] text-gray-900 mb-4">
              Your first mate, always on watch.
            </h2>
            <p className="text-base sm:text-lg text-gray-600 leading-relaxed max-w-2xl mx-auto">
              Every BookingFlows dashboard ships with Bosun — a built-in guide who knows the whole vessel.
              Ask where anything lives, how a feature works, or what to set up next, and Bosun points the way.
            </p>
          </div>

          <div className="max-w-3xl mx-auto">
            {/* Bosun render */}
            <div data-testid="supplier-bosun-chat-mock">
              <img
                src="/images/bf-bosun.webp"
                alt="Ask Bosun — BookingFlows' built-in first mate answering setup questions in chat"
                loading="lazy"
                className="w-full block"
              />
            </div>
            <p className="mt-6 flex items-center justify-center gap-2 text-sm text-gray-500 text-center" data-testid="bosun-trust-line">
              <Lock className="w-3.5 h-3.5 text-[#1F2A5C] shrink-0" />
              Bosun teaches the software only — zero access to your customer data, payments or credentials.
            </p>
          </div>
        </div>
      </section>

      {/* GetMyBoat sync — concise, hands-on "manual" concierge framing (intentionally
          understated; do NOT describe any automation). */}
      <section className="py-24 bg-white" data-testid="gmb-sync-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="rounded-2xl border border-gray-100 bg-gray-50/60 px-6 py-8 sm:px-10 flex flex-col sm:flex-row items-center gap-6 text-center sm:text-left">
            <img src="/images/integrations/getmyboat-logo.png" alt="GetMyBoat" className="h-14 w-14 rounded-xl shadow-sm shrink-0" />
            <div className="flex-1">
              <span className="text-xs font-semibold uppercase tracking-wide text-blue-600">Concierge add-on</span>
              <h2 className="mt-1 text-2xl font-bold text-gray-900">Already on GetMyBoat? One calendar.</h2>
              <p className="mt-2 text-gray-600">
                Our team <span className="font-semibold text-gray-900">manually monitors</span> your GetMyBoat bookings and keeps them in sync with BookingFlows — no double-bookings, no juggling tabs.
              </p>
            </div>
            <button
              onClick={() => { trackSignupStarted('supplier_gmb_section'); navigate('/signup'); }}
              data-testid="supplier-gmb-signup"
              className="group inline-flex items-center gap-2 text-sm font-semibold text-gray-900 hover:text-black transition-colors shrink-0">
              Get started
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            </button>
          </div>
        </div>
      </section>


      {/* ───────────────────────────────────────────────────────────────
          Revenue Insights — truthful, punchy "know your best performers".
          Backed by the real per-asset revenue already shown on the Assets
          page (no "AI/algorithm" overclaim — just clear reporting).
      ─────────────────────────────────────────────────────────────── */}
      <section className="py-24 bg-gray-50" data-testid="supplier-revenue-insights">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16 max-w-3xl mx-auto animate-on-scroll">
            <div className="inline-flex items-center gap-2 text-blue-600 text-xs font-semibold uppercase tracking-[0.2em] mb-4">
              <BarChart3 className="w-3.5 h-3.5" />
              Revenue Insights
            </div>
            <h3 className="text-4xl md:text-5xl font-bold tracking-tight text-gray-900 leading-[1.1] mb-4">
              Know which assets{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">actually pay the bills.</span>
            </h3>
            <p className="text-lg text-gray-500 leading-relaxed">
              See roughly how much revenue each boat, jet ski or vehicle has pulled in over the
              last 12 months — and spot the ones quietly costing you.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-12 items-center">
            {/* Bullets */}
            <div className="space-y-7 animate-on-scroll">
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center flex-shrink-0">
                  <DollarSign className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-1">Revenue per asset, at a glance</h4>
                  <p className="text-sm text-gray-500 leading-relaxed">
                    Every boat, jet ski or vehicle shows its own trailing 12-month revenue —
                    pulled straight from your real bookings, not guesswork.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center flex-shrink-0">
                  <TrendingUp className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-1">Spot your best — and your laggards</h4>
                  <p className="text-sm text-gray-500 leading-relaxed">
                    Instantly see which assets are carrying the fleet and which are barely
                    moving — so you know where to add capacity or cut your losses.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center flex-shrink-0">
                  <SlidersHorizontal className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-1">One toggle, no spreadsheets</h4>
                  <p className="text-sm text-gray-500 leading-relaxed">
                    Flip "Showing revenue" on or off right from your Assets page. No exports,
                    no pivot tables, no end-of-month scramble.
                  </p>
                </div>
              </div>
            </div>

            {/* Mockup — sleek per-asset revenue panel (illustrative demo data) */}
            <div className="relative animate-on-scroll">
              <div className="rounded-[1.75rem] border border-gray-200 bg-white shadow-[0_30px_60px_-32px_rgba(15,23,42,0.28)] overflow-hidden">
                {/* header */}
                <div className="flex items-center justify-between px-6 pt-5 pb-4 border-b border-gray-100/80">
                  <div>
                    <h5 className="font-bold text-gray-900 text-[15px] leading-none tracking-tight">Assets</h5>
                    <p className="text-[11px] text-gray-400 mt-1.5">Ranked by last 12 months</p>
                  </div>
                  <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-blue-700 bg-blue-50 border border-blue-100 px-2.5 py-1.5 rounded-full">
                    <DollarSign className="w-3 h-3" /> Showing revenue
                  </span>
                </div>

                {/* rows */}
                <div className="p-3 sm:p-4 space-y-1.5">
                  {[
                    { name: 'Sunset Cruiser', cap: 14, rev: '$38,420', bar: 100, rank: 1, tint: 'from-blue-500 to-indigo-600', top: true },
                    { name: 'Harbor Pontoon', cap: 12, rev: '$24,180', bar: 63,  rank: 2, tint: 'from-violet-500 to-purple-600' },
                    { name: 'Wave Runner',    cap: 4,  rev: '$3,960',  bar: 10,  rank: 3, tint: 'from-slate-400 to-slate-500', low: true },
                  ].map((a) => (
                    <div
                      key={a.name}
                      className={`group relative flex items-center gap-3.5 rounded-2xl px-3.5 py-3 transition-colors ${a.top ? 'bg-blue-50/70 ring-1 ring-blue-100' : 'hover:bg-gray-50'}`}
                      data-testid={`revenue-asset-${a.name.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      {/* rank */}
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-bold flex-shrink-0 ${a.top ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-500'}`}>
                        {a.rank}
                      </div>
                      {/* icon */}
                      <div className={`w-9 h-9 rounded-xl bg-gradient-to-br ${a.tint} text-white flex items-center justify-center flex-shrink-0 shadow-sm`}>
                        <Anchor className="w-4 h-4" />
                      </div>
                      {/* name + revenue bar */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <span className="font-semibold text-gray-900 text-sm truncate">{a.name}</span>
                          <span className={`font-bold text-sm tabular-nums flex-shrink-0 ${a.low ? 'text-gray-400' : 'text-gray-900'}`}>{a.rev}</span>
                        </div>
                        <div className="flex items-center gap-2 mt-1.5">
                          <div className="h-1.5 flex-1 rounded-full bg-gray-100 overflow-hidden">
                            <div className={`h-full rounded-full bg-gradient-to-r ${a.tint}`} style={{ width: `${a.bar}%` }} />
                          </div>
                          <span className="text-[10px] text-gray-400 flex items-center gap-0.5 flex-shrink-0">
                            <Users className="w-2.5 h-2.5" /> {a.cap}
                          </span>
                        </div>
                      </div>

                      {a.top && (
                        <span className="absolute -top-2 right-3 text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-blue-600 text-white shadow-sm">
                          Top earner
                        </span>
                      )}
                    </div>
                  ))}
                </div>

                {/* footer */}
                <div className="px-6 py-3 border-t border-gray-100/80 flex items-center justify-between">
                  <span className="text-[11px] text-gray-400">Trailing 12-month revenue</span>
                  <span className="text-[11px] font-medium text-amber-600 flex items-center gap-1">
                    <Activity className="w-3 h-3" /> Wave Runner is underperforming
                  </span>
                </div>
              </div>

            </div>
          </div>
        </div>
      </section>


      {/* Customer Ticket Feature */}
      <section className="py-24 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16 max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-2 text-blue-600 text-xs font-semibold uppercase tracking-[0.2em] mb-4">
              <Link2 className="w-3.5 h-3.5" />
              Customer Experience
            </div>
            <h3 className="text-4xl md:text-5xl font-bold tracking-tight text-gray-900 leading-[1.1] mb-4">
              One link,{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">everything they need.</span>
            </h3>
            <p className="text-lg text-gray-500 leading-relaxed">
              Give your customers everything in one tap — no app downloads, no confusion.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-12 items-center">
            {/* Features List */}
            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="bg-blue-500 p-3 rounded-lg flex-shrink-0">
                  <Check className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h4 className="landing-step-title">All Booking Details in One Place</h4>
                  <p className="text-gray-600">
                    Meeting location, time, asset name, guide info, and emergency contacts—everything your customer needs to show up prepared.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="bg-green-500 p-3 rounded-lg flex-shrink-0">
                  <Lock className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h4 className="landing-step-title">PIN-Protected Waiver Access</h4>
                  <p className="text-gray-600">
                    Customers sign waivers digitally using a secure PIN. Your team instantly knows who signed—no paperwork, no hassle.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="bg-purple-500 p-3 rounded-lg flex-shrink-0">
                  <ArrowRight className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h4 className="landing-step-title">Instantly Shareable</h4>
                  <p className="text-gray-600">
                    Send via text, email, or any messaging app. Works on any device—no app required. Customers get the link, they're ready to go.
                  </p>
                </div>
              </div>
            </div>

            {/* Ticket Mockup */}
            <div className="relative">
              <div className="bg-white rounded-2xl shadow-2xl border-l-4 border-blue-600 p-8 max-w-md mx-auto">
                {/* Header */}
                <div className="flex items-center justify-between mb-6 pb-4 border-b">
                  <div className="flex items-center gap-2">
                    <div className="bg-blue-600 p-2 rounded-lg">
                      <Calendar className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-wide">Booking ID</p>
                      <p className="font-mono font-bold text-blue-600">BF-1CWQ4T</p>
                    </div>
                  </div>
                  <span className="text-xs bg-green-100 text-green-800 px-3 py-1 rounded-full font-semibold">Confirmed</span>
                </div>

                {/* Company Name */}
                <h3 className="font-bold text-2xl text-gray-900 mb-6">Austin ATV Rentals</h3>

                {/* Booking Details */}
                <div className="space-y-4 mb-6">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Asset</p>
                      <p className="font-semibold text-gray-900">Sunset Cruiser</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Guide</p>
                      <p className="font-semibold text-gray-900">John S.</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Date</p>
                      <p className="font-semibold text-gray-900">Sat, Nov 15</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Time</p>
                      <p className="font-semibold text-gray-900">2:00 PM</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Duration</p>
                      <p className="font-semibold text-gray-900">3 hours</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Guests</p>
                      <p className="font-semibold text-gray-900">8 people</p>
                    </div>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Meeting Location</p>
                    <p className="font-semibold text-gray-900">Trailhead Parking</p>
                  </div>
                </div>

                {/* Emergency */}
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-4">
                  <p className="text-xs font-semibold text-orange-900 mb-1">Emergency Contact</p>
                  <p className="text-sm font-mono text-orange-700">+1 (512) 555-ASSET</p>
                </div>

                {/* Who's Signed */}
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                  <p className="text-xs font-semibold text-green-900 mb-2 uppercase tracking-wide">Who's Signed the Waiver</p>
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-green-600" />
                      <span className="text-sm text-green-800">John Smith</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-green-600" />
                      <span className="text-sm text-green-800">Sarah Johnson</span>
                    </div>
                    <div className="flex items-center gap-2 opacity-50 mt-2 pt-2 border-t border-green-200">
                      <div className="w-4 h-4 border border-gray-400 rounded"></div>
                      <span className="text-sm text-gray-600">6 more guests need to sign</span>
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="space-y-2">
                  <button className="btn-card-primary">
                    <Lock className="w-4 h-4" />
                    <span>Sign Waiver</span>
                    <span className="absolute right-4 text-xs opacity-75">2/8</span>
                  </button>
                  <button className="w-full bg-gradient-to-r from-yellow-400 to-orange-500 text-white py-3 rounded-lg font-semibold flex items-center justify-center gap-2">
                    <Star className="w-4 h-4 fill-white" />
                    Leave a Review
                  </button>
                </div>
                
                {/* Footer - Copy Link */}
                <div className="text-center mt-6 pt-4 border-t">
                  <button className="text-sm text-gray-500 hover:text-gray-700 font-medium flex items-center justify-center gap-2 mx-auto">
                    <Link2 className="w-4 h-4" />
                    Copy Ticket Link
                  </button>
                </div>
              </div>

              <div className="absolute -bottom-4 -right-4 bg-green-500 text-white px-4 py-2 rounded-full text-sm font-bold shadow-lg">
                Works on any device
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Marketplace Section */}
      <section className="py-24 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Text Content - Centered on top */}
          <div className="text-center mb-12 max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-2 text-blue-600 text-xs font-semibold uppercase tracking-[0.2em] mb-4">
              <Globe className="w-3.5 h-3.5" />
              Expand Your Reach
            </div>
            <h3 className="text-4xl md:text-5xl font-bold tracking-tight text-gray-900 leading-[1.1] mb-4">
              Get listed on our{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">marketplace.</span>
            </h3>
            <p className="text-lg text-gray-500 mb-6 leading-relaxed">
              Reach thousands of travelers actively searching for experiences like yours.
            </p>
            <div className="flex flex-wrap justify-center gap-x-8 gap-y-3 mb-8">
              <div className="flex items-center gap-2">
                <Check className="w-5 h-5 text-blue-600 flex-shrink-0" />
                <span className="text-gray-700">Instant visibility</span>
              </div>
              <div className="flex items-center gap-2">
                <Check className="w-5 h-5 text-blue-600 flex-shrink-0" />
                <span className="text-gray-700">Professional profile</span>
              </div>
              <div className="flex items-center gap-2">
                <Check className="w-5 h-5 text-blue-600 flex-shrink-0" />
                <span className="text-gray-700">SEO-optimized</span>
              </div>
              <div className="flex items-center gap-2">
                <Check className="w-5 h-5 text-blue-600 flex-shrink-0" />
                <span className="text-gray-700">Included free</span>
              </div>
            </div>
            <button
              onClick={() => navigate('/marketplace')}
              className="group inline-flex items-center gap-2 text-sm font-semibold text-[#2563EB] hover:text-[#1D4ED8] transition-colors"
              data-testid="supplier-view-marketplace"
            >
              View Marketplace
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            </button>
          </div>
          
          {/* Visual - Browser Window Mockup below */}
          <div className="relative max-w-3xl mx-auto">
            <div className="bg-white rounded-2xl border border-gray-200 shadow-xl overflow-hidden">
              <div className="bg-gray-100 px-4 py-2 flex items-center gap-2 border-b">
                <div className="flex gap-1.5">
                  <div className="w-3 h-3 rounded-full bg-red-400"></div>
                  <div className="w-3 h-3 rounded-full bg-yellow-400"></div>
                  <div className="w-3 h-3 rounded-full bg-green-400"></div>
                </div>
                <div className="flex-1 text-center">
                  <span className="text-xs text-gray-500">bookingflows.com</span>
                </div>
              </div>
              <div className="p-4 bg-gray-50">
                {/* Marketplace Header */}
                <div className="bg-white rounded-lg p-3 mb-3 border border-gray-100">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <img src="/logo-icon.png" alt="BookingFlows" className="w-6 h-6 rounded" />
                      <span className="font-semibold text-sm text-gray-900">BookingFlows</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="h-6 w-24 bg-gray-100 rounded-full"></div>
                    </div>
                  </div>
                </div>
                {/* Experience Cards Grid - Taller with photos */}
                <div className="grid grid-cols-3 gap-3">
                  <div className="bg-white rounded-lg overflow-hidden border border-gray-100">
                    <div className="h-32 bg-cover bg-center" style={{backgroundImage: "url('https://images.unsplash.com/photo-1664532962042-07b2f58bdd8e?w=400')"}}></div>
                    <div className="p-2">
                      <p className="font-medium text-xs text-gray-900 truncate">Sunset Boat Tour</p>
                      <p className="text-xs text-gray-500">Austin, TX</p>
                      <div className="flex items-center justify-between mt-1">
                        <span className="text-xs font-semibold text-blue-600">$89</span>
                        <div className="flex items-center gap-0.5">
                          <Star className="w-3 h-3 text-yellow-400 fill-yellow-400" />
                          <span className="text-xs">4.9</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="bg-white rounded-lg overflow-hidden border border-gray-100">
                    <div className="h-32 bg-cover bg-center" style={{backgroundImage: "url('/marketplace-jetski.jpg')"}}></div>
                    <div className="p-2">
                      <p className="font-medium text-xs text-gray-900 truncate">Jet Ski Rental</p>
                      <p className="text-xs text-gray-500">Lake Travis</p>
                      <div className="flex items-center justify-between mt-1">
                        <span className="text-xs font-semibold text-blue-600">$120</span>
                        <div className="flex items-center gap-0.5">
                          <Star className="w-3 h-3 text-yellow-400 fill-yellow-400" />
                          <span className="text-xs">4.8</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="bg-white rounded-lg overflow-hidden border border-gray-100">
                    <div className="h-32 bg-cover bg-center" style={{backgroundImage: "url('https://images.unsplash.com/photo-1569263979104-865ab7cd8d13?w=400')"}}></div>
                    <div className="p-2">
                      <p className="font-medium text-xs text-gray-900 truncate">Private Yacht Charter</p>
                      <p className="text-xs text-gray-500">Miami, FL</p>
                      <div className="flex items-center justify-between mt-1">
                        <span className="text-xs font-semibold text-blue-600">$450</span>
                        <div className="flex items-center gap-0.5">
                          <Star className="w-3 h-3 text-yellow-400 fill-yellow-400" />
                          <span className="text-xs">4.7</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="absolute -bottom-3 -right-3 bg-blue-600 text-white px-3 py-1.5 rounded-full text-xs font-semibold shadow-lg">
              Free Listing
            </div>
          </div>
        </div>
      </section>

      {/* Done-for-you Websites — real proof (BOATX + Banana Boat sites we built) */}
      <section className="py-24 bg-white" data-testid="done-for-you-websites">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-2 text-fuchsia-600 text-xs font-semibold uppercase tracking-[0.2em] mb-4">
              <Sparkles className="w-3.5 h-3.5" />
              Done-for-you websites
            </div>
            <h3 className="text-4xl md:text-5xl font-bold tracking-tight text-gray-900 leading-[1.1] mb-4">
              No website?{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-fuchsia-600 to-pink-600">We'll build you one.</span>
            </h3>
            <p className="text-lg text-gray-500 mb-6 leading-relaxed">
              SEO-competitive, fully integrated, and your services live from day one — no templates, no monthly website fees. The same sites that book real customers below were built on BookingFlows.
            </p>
            <div className="flex flex-wrap justify-center gap-x-8 gap-y-3">
              {['SEO-optimized', 'Mobile-first', 'Bookings built in', 'Your brand, your domain'].map((t) => (
                <div key={t} className="flex items-center gap-2">
                  <Check className="w-5 h-5 text-fuchsia-600 flex-shrink-0" />
                  <span className="text-gray-700">{t}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {[
              { name: 'Banana Boat Rentals', domain: 'bananaboatrentals.com', href: '/customers/banana-boat-rentals', cta: 'See the story',
                src: 'https://customer-assets.emergentagent.com/job_d269599a-400d-440c-92a3-9218d3ccd3af/artifacts/1tapvxni_Screenshot%202026-06-12%20at%209.40.35%E2%80%AFPM.png' },
              { name: 'Gee Transportation', domain: 'bookingflowsdemo.com', external: 'https://bookingflowsdemo.com', cta: 'Visit the site',
                src: 'https://customer-assets-0z36b82j.emergentagent.net/job_f27fe237-052e-4979-a423-fd4be621e1f9/artifacts/byeirckx_Screenshot%202026-08-02%20at%2012.56.43%E2%80%AFAM.webp' },
              { name: 'Charter Daze', domain: 'charterdaze.com', external: 'https://charterdaze.com', cta: 'Visit the site',
                src: 'https://customer-assets-0z36b82j.emergentagent.net/job_f27fe237-052e-4979-a423-fd4be621e1f9/artifacts/mi5x5o6u_Screenshot%202026-08-02%20at%201.00.31%E2%80%AFAM.webp' },
            ].map((site) => (
              <div key={site.name} className="group bg-white rounded-2xl border border-gray-200 shadow-xl overflow-hidden hover:shadow-2xl transition-shadow">
                <div className="bg-gray-100 px-4 py-2 flex items-center gap-2 border-b">
                  <div className="flex gap-1.5">
                    <div className="w-3 h-3 rounded-full bg-red-400"></div>
                    <div className="w-3 h-3 rounded-full bg-yellow-400"></div>
                    <div className="w-3 h-3 rounded-full bg-green-400"></div>
                  </div>
                  <div className="flex-1 flex items-center justify-center gap-1.5 text-xs text-gray-400">
                    <Lock className="w-3 h-3" /> {site.domain}
                  </div>
                </div>
                <div className="relative h-64 overflow-hidden bg-gray-50">
                  <img
                    src={proxyImg(site.src, 1000, 80)}
                    alt={`${site.name} website built by BookingFlows`}
                    loading="lazy"
                    decoding="async"
                    className="w-full object-cover object-top group-hover:scale-[1.02] transition-transform duration-500"
                  />
                </div>
                <div className="flex items-center justify-between px-5 py-4">
                  <div>
                    <div className="text-sm font-semibold text-gray-900">{site.name}</div>
                    <div className="text-xs text-gray-400">Live on {site.domain}</div>
                  </div>
                  {site.external ? (
                    <a
                      href={site.external}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 text-sm font-semibold text-fuchsia-600 hover:text-fuchsia-700 transition-colors"
                      data-testid={`dfy-story-${site.name.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      {site.cta} <ArrowRight className="w-4 h-4" />
                    </a>
                  ) : (
                    <button
                      onClick={() => navigate(site.href)}
                      className="inline-flex items-center gap-1.5 text-sm font-semibold text-fuchsia-600 hover:text-fuchsia-700 transition-colors"
                      data-testid={`dfy-story-${site.name.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      {site.cta} <ArrowRight className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <button
              onClick={() => navigate('/demo')}
              className="group inline-flex items-center gap-2 text-sm font-semibold text-gray-700 hover:text-gray-900 transition-colors"
              data-testid="dfy-book-demo"
            >
              Talk to us about a website
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            </button>
          </div>
        </div>
      </section>


      {/* CRM Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div>
              <div className="inline-flex items-center gap-2 text-blue-600 text-xs font-semibold uppercase tracking-[0.2em] mb-4">
                <Users className="w-3.5 h-3.5" />
                Customer Management
              </div>
              <h3 className="text-4xl md:text-5xl font-bold tracking-tight text-gray-900 leading-[1.1] mb-4">
                Every booking{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">builds your CRM.</span>
              </h3>
              <p className="text-lg text-gray-500 mb-6 leading-relaxed">
                Booking history, contact details, preferences — all collected automatically.
              </p>
              <ul className="space-y-3 mb-8">
                <li className="flex items-center gap-3">
                  <Check className="w-5 h-5 text-green-600 flex-shrink-0" />
                  <span className="text-gray-700">Complete booking history per customer</span>
                </li>
                <li className="flex items-center gap-3">
                  <Check className="w-5 h-5 text-green-600 flex-shrink-0" />
                  <span className="text-gray-700">Contact details & preferences</span>
                </li>
                <li className="flex items-center gap-3">
                  <Check className="w-5 h-5 text-green-600 flex-shrink-0" />
                  <span className="text-gray-700">Automated thank you emails</span>
                </li>
                <li className="flex items-center gap-3">
                  <Check className="w-5 h-5 text-green-600 flex-shrink-0" />
                  <span className="text-gray-700">Export data anytime</span>
                </li>
              </ul>
            </div>
            <div className="relative">
              <div className="bg-white rounded-2xl border border-gray-200 shadow-lg p-6">
                <div className="flex items-center justify-between mb-6">
                  <h4 className="font-semibold text-gray-900">Recent Customers</h4>
                  <span className="text-sm text-blue-600 font-medium">View All</span>
                </div>
                <div className="space-y-4">
                  <div className="flex items-center gap-4 p-3 bg-gray-50 rounded-xl">
                    <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                      <span className="text-blue-600 font-semibold text-sm">JS</span>
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-gray-900">John Smith</p>
                      <p className="text-sm text-gray-500">3 bookings • Last: Nov 15</p>
                    </div>
                    <span className="text-green-600 font-semibold">$450</span>
                  </div>
                  <div className="flex items-center gap-4 p-3 bg-gray-50 rounded-xl">
                    <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                      <span className="text-purple-600 font-semibold text-sm">SJ</span>
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-gray-900">Sarah Johnson</p>
                      <p className="text-sm text-gray-500">5 bookings • Last: Nov 12</p>
                    </div>
                    <span className="text-green-600 font-semibold">$825</span>
                  </div>
                  <div className="flex items-center gap-4 p-3 bg-gray-50 rounded-xl">
                    <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                      <span className="text-green-600 font-semibold text-sm">MD</span>
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-gray-900">Mike Davis</p>
                      <p className="text-sm text-gray-500">1 booking • Last: Nov 10</p>
                    </div>
                    <span className="text-green-600 font-semibold">$150</span>
                  </div>
                </div>
                <div className="mt-6 pt-4 border-t border-gray-100">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-500">Total Customers</span>
                    <span className="font-semibold text-gray-900">1,234</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ⚡ Instant Lead Capture — embeddable inquiry form + instant team alerts */}
      <section className="py-24 bg-gray-50" data-testid="supplier-lead-capture-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Visual — white-label replica of the actual lead form + alert story */}
            <div className="order-2 lg:order-1">
              <div className="relative max-w-md mx-auto lg:mx-0 pt-6 pb-8" data-testid="lead-capture-visual">
                {/* The lead form (as it ships — white-label branded) */}
                <div className="bg-white rounded-2xl border border-gray-200 shadow-2xl overflow-hidden">
                  <div className="h-1 bg-gray-100">
                    <div className="h-full w-1/4 bg-orange-500 rounded-r-full"></div>
                  </div>
                  <div className="p-6 sm:p-7">
                    <div className="flex items-center gap-3 mb-5">
                      <div className="w-9 h-9 rounded-lg bg-gray-900 flex items-center justify-center text-white text-sm font-extrabold">YB</div>
                      <div>
                        <div className="text-sm font-extrabold text-gray-900 leading-tight">Your Brand Charters</div>
                        <div className="text-[11px] text-gray-400">Tell us what you're looking for — we'll get right back to you.</div>
                      </div>
                    </div>
                    <div className="text-[10px] font-bold tracking-[0.15em] text-orange-600 uppercase mb-1">Step 1 of 4</div>
                    <div className="text-lg font-extrabold text-gray-900 mb-4">How can we reach you?</div>
                    <div className="space-y-3">
                      <div>
                        <div className="text-[11px] font-bold text-gray-500 mb-1">Your name</div>
                        <div className="border-[1.5px] border-gray-200 rounded-xl px-3.5 py-2.5 text-sm text-gray-900">Jane Smith</div>
                      </div>
                      <div>
                        <div className="text-[11px] font-bold text-gray-500 mb-1">Email</div>
                        <div className="border-[1.5px] border-orange-400 ring-2 ring-orange-100 rounded-xl px-3.5 py-2.5 text-sm text-gray-900">jane@example.com</div>
                      </div>
                      <div className="rounded-xl bg-gray-900 text-white text-center text-sm font-extrabold py-3">Continue →</div>
                    </div>
                    <div className="mt-4 pt-3 border-t border-gray-100 text-center text-[10px] text-gray-400">
                      🔒 Secure · Powered by <span className="font-bold text-gray-500">BookingFlows</span>
                    </div>
                  </div>
                </div>
                {/* Floating: instant crew alert */}
                <div className="absolute -top-1 -right-2 sm:-right-6 bg-gray-900 text-white rounded-xl shadow-xl px-4 py-3 rotate-2">
                  <div className="flex items-center gap-2 text-xs font-bold"><Zap className="w-3.5 h-3.5 text-orange-400" />Crew pinged</div>
                  <div className="text-[10px] font-mono text-gray-400 mt-0.5">email · SMS · WhatsApp · &lt;5 sec</div>
                </div>
                {/* Floating: the payoff */}
                <div className="absolute -bottom-1 -left-2 sm:-left-6 bg-white border border-emerald-200 rounded-xl shadow-xl px-4 py-3 -rotate-2">
                  <div className="flex items-center gap-2 text-xs font-bold text-emerald-700"><Check className="w-3.5 h-3.5" />Booked &amp; paid</div>
                  <div className="text-[10px] font-mono text-gray-400 mt-0.5">straight onto your calendar</div>
                </div>
              </div>
            </div>
            {/* Copy */}
            <div className="order-1 lg:order-2">
              <div className="inline-flex items-center gap-2 text-orange-600 text-xs font-semibold uppercase tracking-[0.2em] mb-6">
                <Zap className="w-3.5 h-3.5" />
                New · Instant Lead Capture
              </div>
              <h3 className="text-4xl md:text-5xl font-bold tracking-tight text-gray-900 leading-[1.12] mb-6">
                Catch every lead.{' '}
                <span className="text-orange-600">Land every booking.</span>
              </h3>
              <p className="text-lg sm:text-xl text-gray-600 leading-relaxed mb-8">
                Put a quick inquiry form on any page of your website. The second it's submitted,
                your crew gets pinged — and the lead can go from "just asking" to booked-and-paid
                without ever waiting on a callback.
              </p>
              <ul className="space-y-3 mb-10">
                {[
                  'Embeddable inquiry form — one line of code, styled to your brand',
                  'Instant alerts by email, text or WhatsApp — you pick who gets pinged, and when',
                  'Every lead lands in Relations with status, follow-ups and conversion tracking',
                  'Automatic follow-up offers — a discount code that reels in the fence-sitters',
                  'Plugs straight into Zapier — a lead.created event fires the moment a form comes in',
                ].map((t) => (
                  <li key={t} className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">{t}</span>
                  </li>
                ))}
              </ul>
              <button
                onClick={() => navigate('/signup')}
                className="group inline-flex items-center gap-2 text-sm font-semibold text-orange-600 hover:text-orange-700 transition-colors"
                data-testid="lead-capture-cta"
              >
                Start capturing leads
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* QR Scanner Section */}
      <section className="py-24 bg-gradient-to-br from-purple-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div className="order-2 md:order-1">
              <div className="flex gap-6 justify-center items-start">
                {/* Ticket with QR Code */}
                <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-4 w-48 flex-shrink-0">
                  <div className="flex items-center gap-2 mb-3 pb-2 border-b">
                    <div className="bg-blue-600 p-1.5 rounded">
                      <Calendar className="w-3 h-3 text-white" />
                    </div>
                    <div>
                      <p className="text-[10px] text-gray-500">Booking ID</p>
                      <p className="text-xs font-bold text-blue-600">BF-ABC123</p>
                    </div>
                  </div>
                  <p className="font-bold text-sm text-gray-900 mb-1">BOATX</p>
                  <p className="text-xs text-gray-500 mb-3">Sat, Nov 29 • 2:00 PM</p>
                  {/* QR Code */}
                  <div className="bg-gray-50 p-3 rounded-xl mb-2">
                    <QrCode className="w-full h-auto text-gray-900" />
                  </div>
                  <p className="text-[10px] text-center text-gray-400">Scan to check in</p>
                </div>

                {/* Phone Scanner */}
                <div className="relative max-w-[200px]">
                  <div className="bg-gray-900 rounded-[2rem] p-2 shadow-2xl">
                    <div className="bg-white rounded-[1.5rem] overflow-hidden">
                      <div className="bg-purple-600 px-4 py-2">
                        <p className="text-white font-semibold text-sm text-center">Scan QR Code</p>
                      </div>
                      <div className="p-3">
                        {/* Scanner Viewfinder with QR */}
                        <div className="relative aspect-square bg-gray-900 rounded-xl mb-3 overflow-hidden">
                          {/* Corner brackets */}
                          <div className="absolute top-2 left-2 w-4 h-4 border-t border-l border-purple-400"></div>
                          <div className="absolute top-2 right-2 w-4 h-4 border-t border-r border-purple-400"></div>
                          <div className="absolute bottom-2 left-2 w-4 h-4 border-b border-l border-purple-400"></div>
                          <div className="absolute bottom-2 right-2 w-4 h-4 border-b border-r border-purple-400"></div>
                          {/* Scan line */}
                          <div className="absolute left-2 right-2 h-0.5 bg-purple-500 top-1/3 animate-pulse shadow-[0_0_10px_rgba(168,85,247,0.8)]"></div>
                          {/* QR Code being scanned */}
                          <div className="absolute inset-0 flex items-center justify-center">
                            <div className="w-16 h-16 bg-white p-1.5 rounded shadow-lg">
                              <QrCode className="w-full h-full text-gray-900" />
                            </div>
                          </div>
                        </div>
                        {/* Success State */}
                        <div className="bg-green-50 border border-green-200 rounded-lg p-2 text-center">
                          <Check className="w-5 h-5 text-green-600 mx-auto mb-1" />
                          <p className="font-semibold text-green-800 text-xs">Verified!</p>
                          <p className="text-[10px] text-green-600">John Smith • 4 guests</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="order-1 md:order-2">
              <div className="inline-flex items-center gap-2 text-blue-600 text-xs font-semibold uppercase tracking-[0.2em] mb-4">
                <QrCode className="w-3.5 h-3.5" />
                QR Check-In
              </div>
              <h3 className="text-4xl md:text-5xl font-bold tracking-tight text-gray-900 leading-[1.1] mb-4">
                Turn any device{' '}
                <span className="block text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">into a scanner.</span>
              </h3>
              <p className="text-gray-600 text-lg mb-6">
                No special hardware needed. Your phone becomes a professional check-in system. Scan customer QR codes and verify bookings instantly.
              </p>
              <ul className="space-y-3 mb-8">
                <li className="flex items-center gap-3">
                  <Check className="w-5 h-5 text-purple-600 flex-shrink-0" />
                  <span className="text-gray-700">Works on any smartphone or tablet</span>
                </li>
                <li className="flex items-center gap-3">
                  <Check className="w-5 h-5 text-purple-600 flex-shrink-0" />
                  <span className="text-gray-700">Instant guest verification</span>
                </li>
                <li className="flex items-center gap-3">
                  <Check className="w-5 h-5 text-purple-600 flex-shrink-0" />
                  <span className="text-gray-700">See booking details immediately</span>
                </li>
                <li className="flex items-center gap-3">
                  <Check className="w-5 h-5 text-purple-600 flex-shrink-0" />
                  <span className="text-gray-700">Track check-ins in real-time</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* White Label Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div>
              <div className="inline-flex items-center gap-2 text-blue-600 text-xs font-semibold uppercase tracking-[0.2em] mb-4">
                <Sparkles className="w-3.5 h-3.5" />
                Your Brand
              </div>
              <h3 className="text-4xl md:text-5xl font-bold tracking-tight text-gray-900 leading-[1.1] mb-4">
                White-label it{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">as your own.</span>
              </h3>
              <p className="text-gray-600 text-lg mb-6">
                Make it yours. Remove our branding completely and present the booking system as your own. Your customers see your brand, not ours.
              </p>
              <ul className="space-y-3 mb-8">
                <li className="flex items-center gap-3">
                  <Check className="w-5 h-5 text-gray-600 flex-shrink-0" />
                  <span className="text-gray-700">Your logo on all customer touchpoints</span>
                </li>
                <li className="flex items-center gap-3">
                  <Check className="w-5 h-5 text-gray-600 flex-shrink-0" />
                  <span className="text-gray-700">Custom colors to match your brand</span>
                </li>
                <li className="flex items-center gap-3">
                  <Check className="w-5 h-5 text-gray-600 flex-shrink-0" />
                  <span className="text-gray-700">No "Powered by" watermarks</span>
                </li>
                <li className="flex items-center gap-3">
                  <Check className="w-5 h-5 text-gray-600 flex-shrink-0" />
                  <span className="text-gray-700">100% free—included with your account</span>
                </li>
              </ul>
              <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                <p className="text-green-800 font-medium">
                  Free of charge. On us. No hidden fees.
                </p>
              </div>
            </div>
            <div className="relative">
              <div className="space-y-4">
                {/* Before */}
                <div className="bg-gray-50 rounded-xl p-4">
                  <p className="text-xs text-gray-500 mb-2 font-medium">BEFORE</p>
                  <div className="bg-white rounded-lg p-4 border border-gray-200">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-8 h-8 bg-blue-600 rounded-lg"></div>
                      <span className="font-semibold text-gray-400">BookingFlows</span>
                    </div>
                    <div className="h-2 bg-gray-200 rounded w-3/4 mb-2"></div>
                    <div className="h-2 bg-gray-100 rounded w-1/2"></div>
                    <div className="mt-3 pt-3 border-t text-xs text-gray-400 text-center">
                      Powered by BookingFlows
                    </div>
                  </div>
                </div>
                {/* After */}
                <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
                  <p className="text-xs text-blue-600 mb-2 font-medium">AFTER (WHITE LABEL)</p>
                  <div className="bg-white rounded-lg p-4 border border-gray-200">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-8 h-8 bg-orange-500 rounded-lg flex items-center justify-center text-white font-bold text-xs">YB</div>
                      <span className="font-semibold text-gray-900">Your Brand</span>
                    </div>
                    <div className="h-2 bg-gray-200 rounded w-3/4 mb-2"></div>
                    <div className="h-2 bg-gray-100 rounded w-1/2"></div>
                    <div className="mt-3 pt-3 border-t text-xs text-gray-900 text-center font-medium">
                      © Your Company Name
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Payments & Global - Combined Stripe + Multi-Currency */}
      <section className="py-24 bg-gradient-to-br from-purple-900 via-indigo-900 to-blue-900 text-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 text-blue-300 text-xs font-semibold uppercase tracking-[0.2em] mb-5">
              <DollarSign className="w-3.5 h-3.5" />
              Payments &amp; Payouts
            </div>
            <div className="flex items-center justify-center mb-6">
              {/* Stripe Logo */}
              <div className="bg-white rounded-xl p-3">
                <svg className="w-16 h-7" viewBox="0 0 60 25" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path fillRule="evenodd" clipRule="evenodd" d="M60 12.8c0-4.28-2.07-7.66-6.03-7.66-3.98 0-6.38 3.38-6.38 7.63 0 5.04 2.84 7.58 6.92 7.58 1.99 0 3.49-.45 4.63-1.09v-3.33c-1.14.57-2.45.93-4.11.93-1.63 0-3.07-.57-3.25-2.56h8.19c0-.22.03-1.08.03-1.5zm-8.19-1.59c0-1.88 1.15-2.67 2.19-2.67 1.02 0 2.1.79 2.1 2.67h-4.29zM41.3 5.14c-1.64 0-2.7.77-3.28 1.3l-.22-1.04H34v19.8l4.22-.9v-4.8c.6.44 1.49 1.06 2.97 1.06 3 0 5.74-2.42 5.74-7.77 0-4.9-2.78-7.65-5.63-7.65zm-.99 11.76c-.99 0-1.57-.36-1.98-.8v-6.3c.44-.48 1.04-.82 1.98-.82 1.52 0 2.57 1.7 2.57 3.94 0 2.3-1.03 3.98-2.57 3.98zM27.65 4.3l4.24-.9V0l-4.24.9v3.4zm0 .98h4.24v14.6h-4.24V5.28zM22.94 6.6l-.26-1.32h-3.63v14.6h4.22v-9.9c1-.3 2.68-1.06 3.2-.88V5.28c-.54-.2-2.51-.57-3.53 1.32zM13.68 1.38L9.56 2.25v13.35c0 2.47 1.85 4.28 4.3 4.28 1.36 0 2.35-.25 2.9-.55v-3.45c-.53.22-3.17 1-3.17-1.5V8.93h3.17V5.28h-3.17l.09-3.9zM4.16 9.84c0-.65.54-.9 1.42-.9 1.27 0 2.87.39 4.14 1.08V6.2c-1.39-.55-2.76-.77-4.14-.77C2.25 5.43 0 7.1 0 10.02c0 4.5 6.2 3.78 6.2 5.72 0 .77-.67 1.02-1.6 1.02-1.39 0-3.17-.57-4.58-1.35v3.82c1.56.67 3.14.96 4.58.96 3.35 0 5.58-1.66 5.58-4.72 0-4.86-6.22-4-6.22-5.63z" fill="#635BFF"/>
                </svg>
              </div>
            </div>
            <h3 className="text-4xl md:text-5xl font-bold tracking-tight mb-4 leading-[1.1]">
              Payments powered by Stripe
            </h3>
            <p className="text-lg text-white/80 max-w-2xl mx-auto">
              Secure payments, instant payouts, and multi-currency support—all handled by one of the most trusted financial platforms in the world.
            </p>
          </div>
          
          {/* Features Grid */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-5 border border-white/10">
              <div className="bg-green-500/20 w-10 h-10 rounded-lg flex items-center justify-center mb-3">
                <Shield className="w-5 h-5 text-green-400" />
              </div>
              <h4 className="font-semibold mb-1">Secure Payments</h4>
              <p className="text-sm text-white/70">Bank-level encryption for every transaction</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-5 border border-white/10">
              <div className="bg-blue-500/20 w-10 h-10 rounded-lg flex items-center justify-center mb-3">
                <Zap className="w-5 h-5 text-blue-400" />
              </div>
              <h4 className="font-semibold mb-1">Instant Payouts</h4>
              <p className="text-sm text-white/70">Money in your account within days</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-5 border border-white/10">
              <div className="bg-purple-500/20 w-10 h-10 rounded-lg flex items-center justify-center mb-3">
                <Globe className="w-5 h-5 text-purple-400" />
              </div>
              <h4 className="font-semibold mb-1">Multi-Currency</h4>
              <p className="text-sm text-white/70">Offer customers multiple currency options</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-5 border border-white/10">
              <div className="bg-orange-500/20 w-10 h-10 rounded-lg flex items-center justify-center mb-3">
                <FileText className="w-5 h-5 text-orange-400" />
              </div>
              <h4 className="font-semibold mb-1">Tax Forms (1099)</h4>
              <p className="text-sm text-white/70">Automated tax reporting handled for you</p>
            </div>
          </div>
          
          {/* Trust badges */}
          <div className="flex flex-wrap justify-center gap-6 text-center text-sm text-white/60">
            <div className="flex items-center gap-2">
              <Lock className="w-4 h-4" />
              <span>PCI Compliant</span>
            </div>
            <div className="flex items-center gap-2">
              <Shield className="w-4 h-4" />
              <span>256-bit SSL</span>
            </div>
            <div className="flex items-center gap-2">
              <Users className="w-4 h-4" />
              <span>Millions of Businesses Trust Stripe</span>
            </div>
          </div>
        </div>
      </section>

      {/* Integrations Section - More Software-y */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-2 text-blue-600 text-xs font-semibold uppercase tracking-[0.2em] mb-4">
              <Link2 className="w-3.5 h-3.5" />
              Integrations
            </div>
            <h3 className="text-4xl md:text-5xl font-bold tracking-tight text-gray-900 leading-[1.1] mb-4">
              We integrate with{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">your channels.</span>
            </h3>
            <p className="text-lg text-gray-500 leading-relaxed">
              Sync your availability and bookings across platforms automatically.
            </p>
          </div>

          {/* Integration Visual */}
          <div className="max-w-4xl mx-auto">
            <div className="relative flex items-center justify-center gap-2 sm:gap-4 md:gap-8">
              {/* Left Platform - GetYourGuide */}
              <div className="flex flex-col items-center min-w-0">
                <div className="bg-white rounded-2xl p-2.5 sm:p-4 border border-green-300 shadow-lg hover:shadow-xl transition-all relative">
                  <div className="absolute -top-2 -right-2 bg-green-500 text-white text-[10px] sm:text-xs font-bold px-1.5 sm:px-2 py-0.5 rounded-full">LIVE</div>
                  <div className="w-12 h-12 sm:w-16 sm:h-16 md:w-20 md:h-20 rounded-xl overflow-hidden">
                    <img src="/images/integrations/gyg-icon.jpg" alt="GetYourGuide" className="w-full h-full object-cover" />
                  </div>
                </div>
                <span className="mt-2 text-xs sm:text-sm font-medium text-gray-700">GetYourGuide</span>
              </div>

              {/* Connection Line with Arrow */}
              <div className="flex items-center flex-shrink-0">
                <div className="w-3 sm:w-6 md:w-12 h-0.5 bg-gradient-to-r from-green-400 to-blue-400"></div>
                <div className="bg-blue-500 rounded-full p-1">
                  <ArrowRight className="w-3 h-3 text-white" />
                </div>
              </div>

              {/* Center - BookingFlows */}
              <div className="flex flex-col items-center min-w-0">
                <div className="bg-gradient-to-br from-blue-600 to-indigo-700 rounded-2xl p-2.5 sm:p-4 shadow-xl border border-blue-400 relative">
                  <div className="absolute -top-2 -right-2 bg-green-500 text-white text-[10px] sm:text-xs font-bold px-1.5 sm:px-2 py-0.5 rounded-full">SYNC</div>
                  <div className="w-12 h-12 sm:w-16 sm:h-16 md:w-20 md:h-20 rounded-xl overflow-hidden bg-white flex items-center justify-center">
                    <img src="/logo-icon.png" alt="BookingFlows" className="w-9 h-9 sm:w-12 sm:h-12 md:w-16 md:h-16 object-contain" />
                  </div>
                </div>
                <span className="mt-2 text-xs sm:text-sm font-bold text-blue-600">BookingFlows</span>
              </div>

              {/* Connection Line with Arrow */}
              <div className="flex items-center flex-shrink-0">
                <div className="bg-blue-500 rounded-full p-1">
                  <ArrowRight className="w-3 h-3 text-white rotate-180" />
                </div>
                <div className="w-3 sm:w-6 md:w-12 h-0.5 bg-gradient-to-l from-green-400 to-blue-400"></div>
              </div>

              {/* Right Platform - LetsBatch */}
              <div className="flex flex-col items-center min-w-0">
                <div className="bg-white rounded-2xl p-2.5 sm:p-4 border border-green-300 shadow-lg hover:shadow-xl transition-all relative">
                  <div className="absolute -top-2 -right-2 bg-green-500 text-white text-[10px] sm:text-xs font-bold px-1.5 sm:px-2 py-0.5 rounded-full">LIVE</div>
                  <div className="w-12 h-12 sm:w-16 sm:h-16 md:w-20 md:h-20 rounded-xl overflow-hidden bg-black p-1 flex items-center justify-center">
                    <img src="/images/integrations/letsbatch-icon.png" alt="LetsBatch" className="w-full h-full object-contain" />
                  </div>
                </div>
                <span className="mt-2 text-xs sm:text-sm font-medium text-gray-700">LetsBatch</span>
              </div>
            </div>

            {/* Coming Soon - Viator */}
            <div className="flex justify-center mt-8">
              <div className="flex items-center gap-3 bg-white border border-dashed border-gray-300 rounded-full px-5 py-2.5">
                <div className="w-8 h-8 rounded-lg overflow-hidden bg-white flex items-center justify-center">
                  <img src="/images/integrations/viator-icon.png" alt="Viator" className="w-full h-full object-contain" />
                </div>
                <span className="text-sm text-gray-600">Viator</span>
                <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-medium">Coming Soon</span>
              </div>
            </div>

            {/* Feature Pills */}
            <div className="flex flex-wrap justify-center gap-3 mt-8">
              <div className="bg-white border border-gray-200 rounded-full px-4 py-2 text-sm flex items-center gap-2 shadow-sm">
                <Check className="w-4 h-4 text-green-500" />
                <span className="text-gray-700">Real-time availability sync</span>
              </div>
              <div className="bg-white border border-gray-200 rounded-full px-4 py-2 text-sm flex items-center gap-2 shadow-sm">
                <Check className="w-4 h-4 text-green-500" />
                <span className="text-gray-700">Automatic booking updates</span>
              </div>
              <div className="bg-white border border-gray-200 rounded-full px-4 py-2 text-sm flex items-center gap-2 shadow-sm">
                <Check className="w-4 h-4 text-green-500" />
                <span className="text-gray-700">No double bookings</span>
              </div>
            </div>
          </div>
        </div>
      </section>



      {/* ───────────────────────────────────────────────────────────────
          Operations Timeline / Audit Trail — every action recorded.
          Accountability story that flows straight into the Security section.
      ─────────────────────────────────────────────────────────────── */}
      <section className="py-24 bg-white animate-on-scroll" data-testid="supplier-audit-trail-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            {/* Left — copy */}
            <div>
              <div className="inline-flex items-center gap-2 text-blue-600 text-xs font-semibold uppercase tracking-[0.2em] mb-4">
                <Clock className="w-3.5 h-3.5" />
                The Log book
              </div>
              <h3 className="text-4xl md:text-5xl font-bold tracking-tight text-gray-900 leading-[1.1] mb-6">
                Every action, recorded.
              </h3>
              <p className="text-xl text-gray-600 leading-relaxed mb-8">
                A full audit trail across your whole operation — your crew knows it as the Log book. Who moved a booking, who approved a refund, who checked a guest in — timestamped, attributed, and always there when you need it.
              </p>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-blue-50 flex items-center justify-center shrink-0 mt-0.5">
                    <Check className="w-3.5 h-3.5 text-blue-600" />
                  </div>
                  <span className="text-gray-700">Every edit, refund, and reassignment attributed to a team member</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-blue-50 flex items-center justify-center shrink-0 mt-0.5">
                    <Check className="w-3.5 h-3.5 text-blue-600" />
                  </div>
                  <span className="text-gray-700">Waivers, payments, and check-ins stamped the second they happen</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-blue-50 flex items-center justify-center shrink-0 mt-0.5">
                    <Check className="w-3.5 h-3.5 text-blue-600" />
                  </div>
                  <span className="text-gray-700">Accountability that protects you in disputes and chargebacks</span>
                </li>
              </ul>
            </div>

            {/* Right — live audit feed mock */}
            <div className="relative">
              <div className="absolute -top-10 -right-8 w-64 h-64 rounded-full bg-blue-100/60 blur-3xl pointer-events-none" />
              <div className="relative bg-white rounded-2xl border border-gray-200 shadow-[0_25px_60px_-25px_rgba(15,23,42,0.25)] overflow-hidden" data-testid="supplier-audit-trail-feed">
                {/* card header */}
                <div className="flex items-center justify-between px-5 py-3.5 border-b border-gray-100 bg-gray-50/70">
                  <div className="flex items-center gap-2">
                    <FileText className="w-4 h-4 text-gray-500" />
                    <span className="text-sm font-semibold text-gray-800">Log book</span>
                  </div>
                  <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-emerald-600">
                    <span className="relative flex h-2 w-2">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
                    </span>
                    Live
                  </span>
                </div>
                {/* entries */}
                <div className="relative px-5 py-4">
                  <div className="absolute left-[38px] top-8 bottom-8 w-px bg-gray-100" />
                  {[
                    { time: '9:42 AM', icon: Users, tint: 'bg-blue-50 text-blue-600', text: <>Ryan reassigned <span className="font-semibold text-gray-900">Booking BK-2194</span> to Captain John</> },
                    { time: '9:45 AM', icon: FileText, tint: 'bg-purple-50 text-purple-600', text: <>Customer <span className="font-semibold text-gray-900">signed waiver</span></> },
                    { time: '9:47 AM', icon: DollarSign, tint: 'bg-emerald-50 text-emerald-600', text: <>Payment <span className="font-semibold text-gray-900">captured</span></> },
                    { time: '10:01 AM', icon: Check, tint: 'bg-teal-50 text-teal-600', text: <>Booking <span className="font-semibold text-gray-900">checked in</span></> },
                    { time: '10:04 AM', icon: Bell, tint: 'bg-amber-50 text-amber-600', text: <>SMS confirmation <span className="font-semibold text-gray-900">delivered</span></> },
                    { time: '10:10 AM', icon: ArrowLeftRight, tint: 'bg-rose-50 text-rose-600', text: <>Refund <span className="font-semibold text-gray-900">approved by Manager</span></> },
                  ].map((e, i) => (
                    <div key={i} className="relative flex items-start gap-3.5 py-2.5">
                      <div className={`relative z-10 w-9 h-9 rounded-full ${e.tint} flex items-center justify-center shrink-0 ring-4 ring-white`}>
                        <e.icon className="w-4 h-4" />
                      </div>
                      <div className="pt-0.5 min-w-0">
                        <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-400 mb-0.5">{e.time}</div>
                        <div className="text-sm text-gray-600 leading-snug">{e.text}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Security & Compliance Section */}
      <section className="py-24 bg-gradient-to-b from-gray-900 to-gray-800 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 text-blue-400 text-xs font-semibold uppercase tracking-[0.2em] mb-4">
              <Shield className="w-3.5 h-3.5" />
              Enterprise-Grade Security
            </div>
            <h3 className="text-4xl md:text-5xl font-bold tracking-tight text-white leading-[1.1] mb-6">
              Your data, protected.
            </h3>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Built for businesses that take privacy seriously. We're working toward the highest compliance standards.
            </p>
          </div>

          {/* Security Features Grid */}
          <div className="grid md:grid-cols-3 gap-8 mb-16">
            {/* Encryption */}
            <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10 hover:border-green-500/30 transition-all">
              <div className="w-14 h-14 bg-green-500/20 rounded-xl flex items-center justify-center mb-6">
                <Lock className="w-7 h-7 text-green-400" />
              </div>
              <h4 className="text-xl font-semibold text-white mb-3">Encrypted at Rest &amp; in Transit</h4>
              <p className="text-gray-400 leading-relaxed">
                <span className="text-green-400 font-medium">AES-256 encryption at rest</span> and <span className="text-green-400 font-medium">TLS 1.3 in transit</span>. Your customer data, payment info, and signed waivers are protected at every step — from browser to database.
              </p>
            </div>

            {/* EU Hosting */}
            <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10 hover:border-blue-500/30 transition-all">
              <div className="w-14 h-14 bg-blue-500/20 rounded-xl flex items-center justify-center mb-6">
                <Shield className="w-7 h-7 text-blue-400" />
              </div>
              <h4 className="text-xl font-semibold text-white mb-3">EU Data Residency</h4>
              <p className="text-gray-400 leading-relaxed">
                Primary databases and APIs hosted in <span className="text-blue-400 font-medium">Frankfurt, Germany</span>—one of the world's strictest data privacy jurisdictions.
              </p>
            </div>

            {/* Compliance */}
            <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10 hover:border-purple-500/30 transition-all">
              <div className="w-14 h-14 bg-purple-500/20 rounded-xl flex items-center justify-center mb-6">
                <FileText className="w-7 h-7 text-purple-400" />
              </div>
              <h4 className="text-xl font-semibold text-white mb-3">Compliance-Ready</h4>
              <p className="text-gray-400 leading-relaxed">
                Building toward <span className="text-purple-400 font-medium">GDPR</span>, <span className="text-purple-400 font-medium">CCPA</span>, and <span className="text-purple-400 font-medium">SOC 2</span> compliance standards. We take privacy regulations seriously.
              </p>
            </div>
          </div>

          {/* Everything-you'd-expect checklist */}
          <div className="mb-16" data-testid="supplier-security-checklist">
            <p className="text-center text-[11px] uppercase tracking-[0.2em] text-gray-500 font-semibold mb-6">
              Everything you'd expect — already on
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {[
                'Daily encrypted backups',
                'Cloudflare edge protection',
                'Stripe PCI-compliant payments',
                'AWS object storage',
                'Role-based permissions',
                'FIDO2 keys, passkeys & 2FA',
                'Audit logs',
                'Automatic SSL',
              ].map((item) => (
                <div key={item} className="flex items-center gap-2.5 bg-white/5 border border-white/10 rounded-xl px-4 py-3">
                  <div className="w-5 h-5 rounded-full bg-green-500/20 flex items-center justify-center shrink-0">
                    <Check className="w-3 h-3 text-green-400" />
                  </div>
                  <span className="text-sm text-gray-200">{item}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Security roadmap — honest "what's next" builds more trust than claiming perfection */}
          <div className="mb-16 text-center" data-testid="supplier-security-roadmap">
            <p className="text-[11px] uppercase tracking-[0.2em] text-gray-500 font-semibold mb-4">
              On the security roadmap
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              {['SOC 2 certification', 'Independent penetration testing', 'Bug bounty program'].map((item) => (
                <span key={item} className="inline-flex items-center gap-2 bg-white/5 border border-white/10 rounded-full px-4 py-2 text-sm text-gray-300">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-400" />
                  {item}
                </span>
              ))}
            </div>
            <button
              onClick={() => navigate('/security')}
              data-testid="supplier-security-page-link"
              className="group mt-6 inline-flex items-center gap-2 text-sm font-semibold text-blue-400 hover:text-blue-300 transition-colors"
            >
              See our full security posture
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            </button>
          </div>

          {/* Reliability stat band */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 pt-10 border-t border-white/10 text-center" data-testid="supplier-reliability-stats">
            <div>
              <div className="text-3xl sm:text-4xl font-bold text-white mb-1">99.9%</div>
              <div className="text-sm text-gray-400">Uptime target</div>
            </div>
            <div>
              <div className="text-3xl sm:text-4xl font-bold text-white mb-1">24/7</div>
              <div className="text-sm text-gray-400">Continuous monitoring &amp; alerts</div>
            </div>
            <div>
              <div className="text-3xl sm:text-4xl font-bold text-white mb-1">Daily</div>
              <div className="text-sm text-gray-400">Encrypted backups, tested recovery</div>
            </div>
            <div>
              <div className="text-3xl sm:text-4xl font-bold text-white mb-1">Global</div>
              <div className="text-sm text-gray-400">CDN + autoscaling infrastructure</div>
            </div>
          </div>
        </div>
      </section>

      {/* 🔑 Enterprise Hardware Keys — physical FIDO2 keys, programmed & shipped
          by us for larger operators. Sits directly under the dark security
          section so the story reads: platform security → hardware upgrade. */}
      <section className="py-24 bg-gradient-to-b from-gray-50 to-white" data-testid="supplier-hardware-keys-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Visual — left on desktop */}
            <div className="relative order-1">
              <img
                src="/images/bf-hardware-keys.webp"
                alt="A FIDO2 hardware security key protecting a BookingFlows organization — key, shield and dashboard connected"
                loading="lazy"
                data-testid="hardware-keys-section-visual"
                className="w-full block"
              />
              <div className="absolute -bottom-4 left-6 inline-flex items-center gap-2 px-3.5 py-2 rounded-full bg-white shadow-lg ring-1 ring-slate-900/[0.06]">
                <span className="w-5 h-5 rounded-md bg-emerald-500 text-white flex items-center justify-center">
                  <Check className="w-3 h-3" strokeWidth={3} />
                </span>
                <span className="text-xs font-semibold text-gray-700">Arrives registered &amp; ready to tap</span>
              </div>
            </div>

            {/* Copy — right on desktop */}
            <div className="order-2">
              <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[#EBF2FF] border border-[#2563EB]/15 mb-6">
                <Key className="w-3.5 h-3.5 text-[#2563EB]" strokeWidth={2.25} />
                <span className="text-[11px] font-bold uppercase tracking-[0.14em] text-[#2563EB]">Enterprise Hardware Keys</span>
              </div>
              <h3 className="text-4xl md:text-5xl font-bold tracking-tight text-gray-900 leading-[1.1] mb-5">
                Phishing can&apos;t touch{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">
                  a physical key.
                </span>
              </h3>
              <p className="text-base sm:text-lg text-gray-600 leading-relaxed mb-7">
                For larger operators, we program and ship <strong className="text-gray-800">FIDO2-certified
                hardware security keys</strong> pre-configured for your team — each key arrives already
                registered to the right account. Hand them out, tap to sign in, done. Even a
                pixel-perfect fake login page can&apos;t capture a hardware key.
              </p>
              <div className="space-y-3 mb-8">
                {[
                  { icon: Package, text: 'Programmed by our engineers and shipped ready to use — zero setup for your team' },
                  { icon: Fingerprint, text: 'FIDO2 / WebAuthn certified — the same phishing-resistant standard we use internally' },
                  { icon: Users, text: 'Pairs with one-toggle org-wide 2FA, so every seat is covered from day one' },
                ].map((line, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center flex-shrink-0">
                      <line.icon className="w-4 h-4" strokeWidth={2} />
                    </div>
                    <span className="text-sm sm:text-base text-gray-700 pt-1.5">{line.text}</span>
                  </div>
                ))}
              </div>
              <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                <Button
                  onClick={() => navigate('/support?category=Hardware%20Security%20Keys#tickets')}
                  data-testid="hardware-keys-section-cta"
                  className="group bg-[#2563EB] hover:bg-[#1D4ED8] text-white rounded-full px-7 h-12 text-sm font-semibold shadow-[0_4px_14px_rgba(38,99,235,0.3)] transition-all"
                >
                  Talk to us about hardware keys
                  <ArrowRight className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-1" />
                </Button>
                <span className="text-xs text-gray-500">Fleet-sized orders · backup keys included</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Crypto Payments — light, compact card-style. Was a heavy dark section
          that double-stacked with Stripe + Security + final CTA. */}
      <section className="py-24 bg-white animate-on-scroll">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center bg-gradient-to-br from-indigo-50 via-white to-blue-50 rounded-3xl border border-gray-100 p-8 sm:p-12">
            {/* Left: Copy */}
            <div>
              <div className="inline-flex items-center gap-2 text-blue-600 text-xs font-semibold uppercase tracking-[0.2em] mb-5">
                <Bitcoin className="w-3.5 h-3.5" />
                Crypto Payments
              </div>
              <h3 className="text-3xl md:text-4xl font-bold tracking-tight text-gray-900 mb-4 leading-[1.1]">
                Accept crypto.{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">Get paid in dollars.</span>
              </h3>
              <p className="text-base text-gray-600 mb-6 leading-relaxed">
                BookingFlows supports USDC stablecoin payments via Stripe. Customers pay with crypto —
                you receive USD with zero volatility risk. Stripe handles conversion and compliance.
              </p>
              <div className="space-y-2.5">
                {[
                  'Customers pay with USDC at checkout',
                  'You receive USD — no crypto knowledge needed',
                  'Works alongside card, Apple Pay, Google Pay',
                ].map((point, i) => (
                  <div key={i} className="flex items-center gap-2.5">
                    <Check className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                    <span className="text-sm text-gray-700">{point}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Right: payment methods card */}
            <div>
              <div className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm">
                <p className="text-[11px] uppercase tracking-[0.18em] text-gray-400 font-semibold mb-4">At Checkout</p>
                <div className="space-y-2.5">
                  {[
                    { label: 'Credit / Debit Card', sub: 'Visa, Mastercard, Amex' },
                    { label: 'Apple Pay / Google Pay', sub: 'One-tap mobile payment' },
                    { label: 'Crypto (USDC)', sub: 'Stablecoin · Settles as USD', badge: 'New' },
                    { label: 'Buy Now, Pay Later', sub: 'Klarna, Affirm' },
                  ].map((m, i) => (
                    <div key={i} className="flex items-center gap-3 p-3 rounded-xl border border-gray-100 bg-gray-50/50">
                      <div className="w-2 h-2 rounded-full bg-emerald-400 flex-shrink-0"></div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-medium text-gray-900">{m.label}</p>
                          {m.badge && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-bold">
                              {m.badge}
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-gray-500">{m.sub}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-24 relative overflow-hidden bg-gradient-to-b from-white via-gray-50 to-white">
        <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
          <div className="absolute top-20 -left-28 w-80 h-80 bg-blue-100/40 rounded-full blur-3xl -z-10" />
          <div className="absolute bottom-24 -right-24 w-72 h-72 bg-slate-200/30 rounded-full blur-3xl -z-10" />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 text-blue-600 text-xs font-semibold uppercase tracking-[0.2em] mb-4">
              <BadgeDollarSign className="w-3.5 h-3.5" />
              Simple Pricing
            </div>
            <h3 className="text-4xl md:text-5xl font-bold tracking-tight text-gray-900 leading-[1.1] mb-4">
              We only make money{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">when you do.</span>
            </h3>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              No monthly fees. No setup costs. Just a small fee added to each booking—paid by your customer, not you.
            </p>
          </div>

          <div className="max-w-sm mx-auto">
            {/* Pricing Breakdown Card */}
            <div className="relative">
            <div className="absolute -top-4 left-1/2 -translate-x-1/2 z-10 inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-slate-900 text-white text-[11px] font-bold shadow-lg whitespace-nowrap">
              <FileText className="w-3 h-3" /> Example booking
            </div>
            <Card className="bg-white border-0 ring-1 ring-slate-900/[0.06] shadow-[0_30px_60px_-25px_rgba(15,23,42,0.3)] rounded-3xl overflow-hidden">
              <CardContent className="p-0">
                {/* Receipt Style Breakdown */}
                <div className="px-7 pt-9 pb-6" style={{ fontVariantNumeric: 'tabular-nums' }}>
                  <div className="space-y-3 text-sm">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-500">Service</span>
                      <span className="font-semibold text-gray-900">$350.00</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-500">Add-ons</span>
                      <span className="font-semibold text-gray-900">+$50.00</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-500">Tax</span>
                      <span className="font-semibold text-gray-900">+$33.00</span>
                    </div>
                    <div className="flex justify-between items-center pt-3 border-t border-dashed border-gray-200">
                      <span className="inline-flex items-center gap-2 text-gray-500">
                        BookingFlows
                        <span className="px-1.5 py-0.5 rounded-md bg-[#EBF2FF] text-[#2563EB] text-[10px] font-bold">3%</span>
                      </span>
                      <span className="font-semibold text-[#2563EB]">+$13.00</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-400">Stripe processing*</span>
                      <span className="font-semibold text-gray-400">+$13.32</span>
                    </div>
                  </div>
                  {/* Total */}
                  <div className="mt-5 pt-4 border-t-2 border-slate-900">
                    <div className="flex justify-between items-center">
                      <span className="font-bold text-gray-900">Customer pays</span>
                      <span className="font-extrabold text-gray-900 text-xl">$459.32</span>
                    </div>
                    <p className="text-[11px] text-gray-400 mt-1">Fees are added on top — paid by your customer, not you.</p>
                  </div>
                  {/* You keep */}
                  <div className="mt-5 rounded-2xl bg-gradient-to-br from-emerald-50 to-teal-50 ring-1 ring-emerald-500/20 p-4">
                    <div className="flex justify-between items-center">
                      <div>
                        <div className="flex items-center gap-1.5 font-bold text-emerald-800">
                          <Check className="w-4 h-4" strokeWidth={3} /> You keep
                        </div>
                        <p className="text-[11px] text-emerald-600/80 mt-0.5">Service + add-ons + tax — every cent</p>
                      </div>
                      <span className="font-extrabold text-emerald-700 text-2xl">$433.00</span>
                    </div>
                  </div>
                  {/* Stripe footnote */}
                  <p className="text-[11px] text-gray-400 mt-4">
                    *Stripe charges 2.9% + 30¢ per transaction — paid straight to Stripe, never to us.
                  </p>
                </div>
                {/* CTA */}
                <div className="px-7 pb-7">
                  <Button 
                    className="btn-supplier-primary w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-medium py-4 rounded-full shadow-sm"
                    onClick={() => { trackSignupStarted('supplier_pricing'); navigate('/signup'); }}
                    data-testid="supplier-pricing-cta"
                  >
                    Get Started <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </div>
              </CardContent>
            </Card>
            </div>
            
            <p className="text-center text-sm text-gray-500 mt-4">
              No monthly fees • No setup costs • Cancel anytime
            </p>
          </div>
        </div>
      </section>

      {/* Custom feature commitment — sincere, human, low-pressure. Positions
          BookingFlows as a real engineering partner rather than rigid SaaS.
          Sits right before the final sign-up CTA so the last impression is
          "they'll actually listen to me." */}
      <section className="py-24 bg-gradient-to-b from-white via-blue-50/50 to-white relative overflow-hidden animate-on-scroll">
        <div className="absolute top-1/4 -left-32 w-96 h-96 bg-blue-200/25 rounded-full blur-3xl -z-0" aria-hidden="true" />
        <div className="absolute bottom-1/4 -right-20 w-80 h-80 bg-indigo-200/25 rounded-full blur-3xl -z-0" aria-hidden="true" />

        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-[1.1fr_1fr] gap-10 lg:gap-14 items-center">
            {/* Copy */}
            <div>
              <div className="inline-flex items-center gap-2 text-blue-600 text-xs font-semibold uppercase tracking-[0.2em] mb-5">
                <Sparkles className="w-3.5 h-3.5" />
                We Build What You Need
              </div>
              <h3 className="text-4xl md:text-5xl font-bold tracking-tight text-gray-900 leading-[1.1] mb-5">
                Need something custom?{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">
                  Just ask.
                </span>
              </h3>
              <p className="text-base sm:text-lg text-gray-600 leading-relaxed mb-7">
                Every operator runs their business a little differently — and BookingFlows
                is built by an operator who gets that. If you need a workflow, integration,
                report, or feature that doesn't exist yet, tell us. We genuinely consider
                every request and ship the ones that make your business better.
              </p>

              <div className="space-y-3 mb-8">
                {[
                  'Real engineers read every request — it goes to builders, not a black hole',
                  'Roadmap shaped by operator feedback, not investor decks',
                  'New feature shipped? Available to every customer, free.',
                ].map((line, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <div className="w-5 h-5 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="w-3 h-3" strokeWidth={3} />
                    </div>
                    <span className="text-sm sm:text-base text-gray-700">{line}</span>
                  </div>
                ))}
              </div>

              <div className="flex flex-wrap items-center gap-4">
                <button
                  type="button"
                  onClick={() => navigate('/support?category=Feature%20request#tickets')}
                  className="group inline-flex items-center gap-2 text-sm sm:text-base font-semibold text-[#2563EB] hover:text-[#1D4ED8] transition-colors"
                  data-testid="supplier-custom-feature-cta"
                >
                  Submit a feature request
                  <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                </button>
                <button
                  type="button"
                  onClick={() => navigate('/support')}
                  className="text-sm text-gray-600 hover:text-gray-900 underline-offset-4 hover:underline transition-colors"
                  data-testid="supplier-custom-feature-support-link"
                >
                  or browse the Support Center
                </button>
              </div>
            </div>

            {/* Visual — friendly inbox-style mock so the message lands as
                a real conversation, not a marketing pitch. */}
            <div className="relative">
              <div className="bg-white rounded-2xl border border-gray-100 shadow-xl shadow-blue-500/5 overflow-hidden">
                <div className="px-5 py-3 border-b border-gray-100 flex items-center gap-2 bg-gray-50/50">
                  <div className="flex gap-1.5">
                    <div className="w-2.5 h-2.5 rounded-full bg-red-400" />
                    <div className="w-2.5 h-2.5 rounded-full bg-amber-400" />
                    <div className="w-2.5 h-2.5 rounded-full bg-emerald-400" />
                  </div>
                  <span className="text-[11px] text-gray-400 ml-2 font-medium">Support Center · Feature requests</span>
                </div>
                <div className="p-5 space-y-4">
                  {/* Inbound */}
                  <div className="flex gap-3">
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 text-white flex items-center justify-center text-xs font-bold flex-shrink-0">SK</div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[11px] text-gray-400 mb-1">Sarah · Charter operator, Dubai · 2 days ago</div>
                      <div className="bg-gray-50 rounded-xl rounded-tl-sm p-3 text-sm text-gray-700 leading-relaxed">
                        Any chance you could add VAT-compliant invoicing for our UAE clients? We need it on every receipt.
                      </div>
                    </div>
                  </div>
                  {/* Outbound — our reply */}
                  <div className="flex gap-3 flex-row-reverse">
                    <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-blue-600 to-indigo-600 text-white flex items-center justify-center flex-shrink-0">
                      <img src="/logo-icon.png" alt="BookingFlows" className="w-7 h-7 rounded-lg object-cover" />
                    </div>
                    <div className="flex-1 min-w-0 text-right">
                      <div className="text-[11px] text-gray-400 mb-1">BookingFlows · just now</div>
                      <div className="bg-gradient-to-br from-blue-600 to-indigo-600 text-white rounded-xl rounded-tr-sm p-3 text-sm leading-relaxed text-left">
                        On it. We'll have a draft for you by Friday — VAT line item on receipts + tenant-level tax config. We'll loop you in on the rollout.
                      </div>
                      <div className="flex items-center gap-1.5 justify-end mt-2 text-[11px] text-emerald-600 font-medium">
                        <Check className="w-3 h-3" strokeWidth={3} />
                        Shipped Friday
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <p className="text-center text-xs text-gray-400 mt-4">
                Real conversations. Real engineering. Real responsiveness.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Mobile App Section — operator-focused, mirrors /marketplace styling */}
      <section className="py-24 px-4 bg-white animate-on-scroll" data-testid="supplier-app-store-section">
        <div className="max-w-5xl mx-auto">
          <div className="bg-gradient-to-br from-blue-600 to-indigo-700 rounded-3xl overflow-hidden">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center p-8 md:p-12">
              <div className="text-white">
                <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/15 text-xs font-semibold tracking-wide mb-5">
                  <Download className="w-3.5 h-3.5" /> NOW ON iOS
                </span>
                <h2 className="text-2xl sm:text-3xl font-bold mb-4">
                  Take BookingFlows with you
                </h2>
                <p className="text-blue-100 mb-6">
                  Run your boat business from your pocket. Manage bookings, check your daily schedule, and get instant alerts the moment a new booking comes in — wherever you are.
                </p>
                <div className="flex flex-wrap gap-4">
                  {/* App Store Button — real link */}
                  <a
                    href="https://apps.apple.com/us/app/bookingflows/id6757174499"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 bg-black hover:bg-gray-900 px-5 py-3 rounded-xl transition-colors"
                    data-testid="supplier-app-store-link"
                  >
                    <svg className="w-8 h-8" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                    </svg>
                    <div className="text-left">
                      <p className="text-xs text-white/70">Download on the</p>
                      <p className="text-sm font-semibold">App Store</p>
                    </div>
                  </a>

                  {/* Play Store Button — coming soon */}
                  <div className="flex items-center gap-3 bg-black/40 px-5 py-3 rounded-xl cursor-not-allowed opacity-75">
                    <svg className="w-8 h-8" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z"/>
                    </svg>
                    <div className="text-left">
                      <p className="text-xs text-white/70">Coming Soon</p>
                      <p className="text-sm font-semibold">Google Play</p>
                    </div>
                  </div>
                </div>

                {/* What you can do — under app buttons */}
                <div className="mt-8 pt-6 border-t border-white/20">
                  <p className="text-sm font-medium text-white mb-4">What you can do</p>
                  <div className="flex flex-col gap-3 text-sm text-blue-100">
                    <span className="flex items-center gap-2">
                      <Activity className="w-4 h-4" />
                      Manage and reassign bookings on the go
                    </span>
                    <span className="flex items-center gap-2">
                      <Calendar className="w-4 h-4" />
                      See your daily schedule at a glance
                    </span>
                    <span className="flex items-center gap-2">
                      <Bell className="w-4 h-4" />
                      Instant push alerts the moment a booking lands
                    </span>
                  </div>
                </div>
              </div>

              {/* Phone mockup */}
              <div className="hidden md:flex justify-center">
                <div className="relative">
                  <div className="w-64 h-[500px] bg-gray-900 rounded-[3rem] p-2 shadow-2xl">
                    <div className="w-full h-full bg-white rounded-[2.5rem] overflow-hidden flex items-center justify-center">
                      <div className="text-center p-6">
                        <img
                          src="/logo-icon.png"
                          alt="BookingFlows"
                          className="w-20 h-20 mx-auto mb-4 rounded-2xl shadow-lg"
                        />
                        <p className="font-bold text-gray-900 text-lg">BookingFlows</p>
                        <p className="text-gray-500 text-sm mt-1">Your fleet, in your pocket</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>


      {/* ───────────────────────────────────────────────────────────────
          Built in the USA & UAE — dual-HQ trust section. Two flag cards
          plus a follow-the-sun support strip.
      ─────────────────────────────────────────────────────────────── */}
      <section className="py-24 bg-white animate-on-scroll" data-testid="supplier-usa-uae-section">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <div className="inline-flex items-center gap-2 text-blue-600 text-xs font-semibold uppercase tracking-[0.2em] mb-4">
              <Globe className="w-3.5 h-3.5" />
              Two Continents. One Team.
            </div>
            <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4 tracking-tight">
              Proudly built in the USA &amp; UAE
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto leading-relaxed">
              BookingFlows is engineered by a team spanning the United States and the United Arab Emirates — built on the water, for operators on the water, in two of the world's biggest boating markets.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-10">
            {/* USA card */}
            <div className="group relative rounded-3xl border border-gray-200 bg-gradient-to-b from-gray-50 to-white p-8 overflow-hidden hover:shadow-xl hover:border-blue-200 transition-all duration-300" data-testid="usa-hq-card">
              <div className="flex items-start justify-between mb-6">
                <img
                  src="https://customer-assets.emergentagent.com/job_a2831e1f-cd94-4900-9f8b-f2b06f8d9d25/artifacts/oxdfkldx_Flag-United-States-of-America.webp"
                  alt="Flag of the United States"
                  className="w-20 h-[54px] rounded-lg object-cover shadow-md ring-1 ring-gray-200 group-hover:scale-105 transition-transform duration-300"
                  loading="lazy"
                />
                <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-blue-700 bg-blue-50 border border-blue-100 rounded-full px-3 py-1">
                  <MapPin className="w-3 h-3" /> Austin, Texas
                </span>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">United States</h3>
              <p className="text-gray-600 leading-relaxed mb-5">
                Product, engineering, and operator success — headquartered on Lake Travis, working side-by-side with US charter fleets, party-boat operators, and tour companies every day.
              </p>
              <ul className="space-y-2">
                <li className="flex items-center gap-2 text-sm text-gray-700"><Check className="w-4 h-4 text-blue-600 flex-shrink-0" /> US-based payments &amp; Tap to Pay support</li>
                <li className="flex items-center gap-2 text-sm text-gray-700"><Check className="w-4 h-4 text-blue-600 flex-shrink-0" /> Built alongside real Texas boat operators</li>
              </ul>
            </div>

            {/* UAE card */}
            <div className="group relative rounded-3xl border border-gray-200 bg-gradient-to-b from-gray-50 to-white p-8 overflow-hidden hover:shadow-xl hover:border-emerald-200 transition-all duration-300" data-testid="uae-hq-card">
              <div className="flex items-start justify-between mb-6">
                <img
                  src="https://customer-assets.emergentagent.com/job_a2831e1f-cd94-4900-9f8b-f2b06f8d9d25/artifacts/vjzngumg_Flag-United-Arab-Emirates.webp"
                  alt="Flag of the United Arab Emirates"
                  className="w-20 h-[54px] rounded-lg object-cover shadow-md ring-1 ring-gray-200 group-hover:scale-105 transition-transform duration-300"
                  loading="lazy"
                />
                <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-full px-3 py-1">
                  <MapPin className="w-3 h-3" /> Dubai, UAE
                </span>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">United Arab Emirates</h3>
              <p className="text-gray-600 leading-relaxed mb-5">
                Serving the Gulf's fast-growing charter scene from Dubai — multi-currency, AED-ready, and tuned for the marinas, yachts, and desert-to-sea experiences of the Emirates.
              </p>
              <ul className="space-y-2">
                <li className="flex items-center gap-2 text-sm text-gray-700"><Check className="w-4 h-4 text-emerald-600 flex-shrink-0" /> AED pricing &amp; Gulf-region operators</li>
                <li className="flex items-center gap-2 text-sm text-gray-700"><Check className="w-4 h-4 text-emerald-600 flex-shrink-0" /> WhatsApp-first customer communication</li>
              </ul>
            </div>
          </div>

          {/* Follow-the-sun strip */}
          <div className="rounded-2xl border border-gray-200 bg-gray-50 px-6 py-5 flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-8 text-center sm:text-left" data-testid="follow-the-sun-strip">
            <div className="flex items-center gap-2 text-gray-900 font-semibold">
              <Clock className="w-5 h-5 text-blue-600" />
              Support that follows the sun
            </div>
            <p className="text-sm text-gray-600">
              With teams 9 time zones apart, someone is always awake when your customers are booking — from Austin sunsets to Dubai sunrises.
            </p>
          </div>
        </div>
      </section>

      {/* Referral Program teaser — keyword: "earn referral commission" */}
      <section className="relative py-24 overflow-hidden bg-gradient-to-br from-emerald-950 via-[#04291f] to-black text-white" data-testid="supplier-referral-section">
        <div className="absolute inset-0 opacity-[0.12] pointer-events-none"
             style={{ backgroundImage: "radial-gradient(circle at 1px 1px, rgba(52,211,153,0.55) 1px, transparent 0)", backgroundSize: "30px 30px" }} />
        <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-emerald-400/15 blur-3xl pointer-events-none" />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-400/15 border border-emerald-300/25 text-[11px] font-semibold uppercase tracking-widest text-emerald-200 mb-6">
              <DollarSign className="w-3.5 h-3.5" /> The Referral Program
            </div>
            <h2 className="text-4xl sm:text-5xl font-bold tracking-tight leading-[1.05] mb-5">
              Know an operator?{' '}
              <span className="bg-gradient-to-r from-emerald-300 to-teal-300 bg-clip-text text-transparent">Earn up to 40%.</span>
            </h2>
            <p className="text-lg text-gray-300 leading-relaxed mb-6">
              Refer a company to BookingFlows and earn a share of the profit we make from them for their entire
              first year. <span className="text-white font-semibold">You don't need to own a company — you don't even need to be a customer.</span>{' '}
              A captain who doesn't own a company? Perfect.
            </p>
            <div className="flex flex-wrap gap-2 mb-8" data-testid="referral-teaser-tiers">
              {[["1st","10%"],["2nd","20%"],["3rd","30%"],["4th+","40%"]].map(([n,pct]) => (
                <div key={n} className="flex items-baseline gap-1.5 rounded-xl bg-white/5 border border-white/10 px-3.5 py-2">
                  <span className="text-[11px] uppercase tracking-wider text-emerald-300/80">{n}</span>
                  <span className="text-lg font-bold text-white">{pct}</span>
                </div>
              ))}
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={() => navigate('/referral-program')}
                data-testid="supplier-referral-cta"
                className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full bg-emerald-400 text-black font-semibold hover:bg-emerald-300 transition-colors"
              >
                Start earning <ArrowRight className="w-4 h-4" />
              </button>
              <button
                onClick={() => navigate('/blog/referral-program')}
                data-testid="supplier-referral-learn"
                className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full border border-white/20 text-white font-semibold hover:bg-white/10 transition-colors"
              >
                How it works
              </button>
            </div>
          </div>
          <div className="relative">
            <img
              src="/images/bf-referral.webp"
              alt="BookingFlows referral program — earn 10% to 40% for referring boating operators"
              className="w-full rounded-2xl border border-white/10 shadow-2xl"
              loading="lazy"
              data-testid="supplier-referral-visual"
            />
            <div className="absolute -bottom-4 -left-4 hidden sm:flex items-center gap-2 rounded-xl bg-white text-gray-900 px-4 py-2.5 shadow-xl">
              <Handshake className="w-4 h-4 text-emerald-600" />
              <span className="text-sm font-semibold">Paid for 12 months</span>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section — keyword: "booking software sign up" */}
      <section className="relative bg-gradient-to-br from-gray-900 via-slate-900 to-gray-900 py-32 overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-10 left-10 w-96 h-96 bg-purple-500 rounded-full blur-3xl"></div>
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-indigo-500 rounded-full blur-3xl"></div>
        </div>

        <div className="max-w-4xl mx-auto text-center px-4 relative z-10">
          <div className="inline-flex items-center gap-2 text-blue-300 text-xs font-semibold uppercase tracking-[0.2em] mb-6">
            <Star className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />
            Top-Rated Booking Software
          </div>
          <h2 className="text-5xl md:text-6xl font-bold text-white mb-6 leading-tight">
            Sign up for booking software<br className="hidden sm:block" />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-400"> — free to start</span>
          </h2>
          <p className="text-xl text-gray-300 mb-4 max-w-2xl mx-auto leading-relaxed">
            Join hundreds of tour operators, charter companies, and rental businesses taking bookings online 24/7.
          </p>
          <p className="text-sm text-blue-300 mb-10">
            After you sign up, our team will reach out personally to help you get set up.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              onClick={() => { trackSignupStarted('supplier_cta_bottom'); navigate('/signup'); }}
              className="bg-gradient-to-r from-blue-600 to-cyan-500 hover:from-blue-700 hover:to-cyan-600 text-white text-xl px-12 py-6 rounded-full font-medium shadow-2xl shadow-blue-500/30 transform hover:scale-105 transition-all duration-300 h-auto"
              data-testid="supplier-cta-bottom"
            >
              Sign Up for Booking Software <ArrowRight className="ml-3 w-6 h-6 inline" />
            </Button>
            <Button
              onClick={() => navigate('/demo')}
              variant="outline"
              className="bg-white/5 border-white/20 text-white hover:bg-white/10 hover:text-white text-xl px-12 py-6 rounded-full font-medium h-auto backdrop-blur"
              data-testid="supplier-cta-demo"
            >
              Book a Free Demo
            </Button>
          </div>
          <p className="text-gray-500 text-sm mt-6">No credit card required · Free to start · Free data migration · Setup in 15 minutes</p>
        </div>
      </section>

      {/* Footer — full footer mirroring /marketplace for brand congruence.
          Includes social links + customer story link + marketplace cross-link. */}
      <footer className="bg-gray-900 text-gray-400 py-14 pb-[calc(3.5rem+env(safe-area-inset-bottom))]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-8 mb-12">
            {/* Brand blurb */}
            <div className="col-span-2 md:col-span-1">
              <div className="flex items-center gap-2 mb-4">
                <img
                  src="/logo-icon.png"
                  alt="BookingFlows"
                  className="w-9 h-9 rounded-xl object-cover"
                />
                <h4 className="text-white font-bold text-lg">BookingFlows</h4>
              </div>
              <p className="text-sm leading-relaxed">
                Online booking software for tours, venues, experiences, and any business that takes reservations.
              </p>
              {/* Social row — same set as /marketplace */}
              <div className="mt-5">
                <SocialLinksDark />
              </div>
            </div>

            {/* Product */}
            <div>
              <h5 className="text-white font-semibold mb-4">Product</h5>
              <ul className="space-y-2 text-sm">
                <li><a href="#features" className="hover:text-white transition-colors">Features</a></li>
                <li><a href="#pricing" className="hover:text-white transition-colors">Pricing</a></li>
                <li><button onClick={() => navigate('/customers/boatx')} className="hover:text-white transition-colors text-left">BOATX story</button></li>
                <li><button onClick={() => navigate('/customers/banana-boat-rentals')} className="hover:text-white transition-colors text-left">Banana Boat Rentals story</button></li>
                <li><button onClick={() => navigate('/marketplace')} className="hover:text-white transition-colors text-left">Marketplace</button></li>
              </ul>
            </div>

            {/* Software */}
            <div>
              <h5 className="text-white font-semibold mb-4">Software</h5>
              <ul className="space-y-2 text-sm">
                <li><button onClick={() => navigate('/for-suppliers')} className="hover:text-white transition-colors text-left">Booking Software</button></li>
                <li><button onClick={() => navigate('/for-charters')} className="hover:text-white transition-colors text-left">Charter Booking</button></li>
                <li><button onClick={() => navigate('/for-tours')} className="hover:text-white transition-colors text-left">Tour Booking</button></li>
                <li><button onClick={() => navigate('/for-rentals')} className="hover:text-white transition-colors text-left">Rental Booking</button></li>
              </ul>
            </div>

            {/* Resources */}
            <div>
              <h5 className="text-white font-semibold mb-4">Resources</h5>
              <ul className="space-y-2 text-sm">
                <li><button onClick={() => navigate('/demo')} className="hover:text-white transition-colors text-left" data-testid="footer-demo-link">Book a Demo</button></li>
                <li><button onClick={() => navigate('/referral-program')} className="hover:text-white transition-colors text-left" data-testid="footer-referral-link">Referral Program</button></li>
                <li><button onClick={() => navigate('/onboarding')} className="hover:text-white transition-colors text-left" data-testid="footer-onboarding-link">Onboarding Guide</button></li>
                <li><button onClick={() => navigate('/developers')} className="hover:text-white transition-colors text-left" data-testid="footer-api-docs-link">API Docs</button></li>
                <li><button onClick={() => navigate('/find-booking')} className="hover:text-white transition-colors text-left">Find My Booking</button></li>
                <li><button onClick={() => navigate('/login')} className="hover:text-white transition-colors text-left">Operator Login</button></li>
                <li><button onClick={() => { trackSignupStarted('footer'); navigate('/signup'); }} className="hover:text-white transition-colors text-left">Get Started Free</button></li>
              </ul>
            </div>

            {/* Support / Legal */}
            <div>
              <h5 className="text-white font-semibold mb-4">Support</h5>
              <ul className="space-y-2 text-sm">
                <li><button onClick={() => navigate('/blog')} className="hover:text-white transition-colors text-left" data-testid="footer-blog-link">Blog</button></li>
                <li><button onClick={() => navigate('/support')} className="hover:text-white transition-colors text-left" data-testid="footer-help-center-link">Help Center</button></li>
                <li><a href="/support" className="hover:text-white transition-colors" data-testid="footer-contact-link">Contact Us</a></li>
                <li><button onClick={() => navigate('/privacy')} className="hover:text-white transition-colors text-left">Privacy Policy</button></li>
                <li><button onClick={() => navigate('/terms')} className="hover:text-white transition-colors text-left">Terms of Service</button></li>
                <li><button onClick={() => navigate('/delete-account')} data-testid="footer-delete-account-link" className="hover:text-white transition-colors text-left">Delete Account</button></li>
                <li><button onClick={() => navigate('/cookies')} className="hover:text-white transition-colors text-left" data-testid="footer-cookies-link">Cookie Policy</button></li>
                <li><button onClick={() => navigate('/dpa')} className="hover:text-white transition-colors text-left" data-testid="footer-dpa-link">Data Processing (DPA)</button></li>
                <li><button onClick={() => navigate('/acceptable-use')} className="hover:text-white transition-colors text-left" data-testid="footer-aup-link">Acceptable Use</button></li>
                <li><button onClick={() => navigate('/security-policy')} className="hover:text-white transition-colors text-left" data-testid="footer-security-policy-link">Security Policy</button></li>
                <li><button onClick={() => navigate('/vulnerability-disclosure')} className="hover:text-white transition-colors text-left" data-testid="footer-vdp-link">Vulnerability Disclosure</button></li>
                <li><button onClick={() => navigate('/dmca')} className="hover:text-white transition-colors text-left" data-testid="footer-dmca-link">DMCA</button></li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-800 pt-8 flex flex-col sm:flex-row items-center justify-between gap-3 text-sm">
            <p>&copy; {new Date().getFullYear()} BookingFlows. All rights reserved.</p>
            <p className="text-gray-500">Built by operators · For operators</p>
          </div>
          <p className="mt-4 text-xs text-gray-600 text-center sm:text-left">
            Intuit and QuickBooks are registered trademarks of Intuit Inc. Used with permission.
          </p>
        </div>
      </footer>

      {/* Floating "Start free" CTA — appears only after the user scrolls past
          the hero. Bottom-right, mobile-safe (honors iOS bottom inset). Pure
          conversion nudge — no chat, no socials, no bot to monitor. */}
      <div
        className={`fixed right-4 sm:right-6 z-40 transition-all duration-300 ease-out pointer-events-none ${
          showFloatingCta ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
        }`}
        style={{ bottom: 'calc(1rem + env(safe-area-inset-bottom, 0px))' }}
      >
        <button
          onClick={() => { trackSignupStarted('floating_cta'); navigate('/signup'); }}
          className="pointer-events-auto group inline-flex items-center gap-2 pl-5 pr-4 py-3 rounded-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white text-sm font-semibold shadow-2xl shadow-blue-500/40 hover:shadow-blue-500/50 transition-all hover:-translate-y-0.5"
          data-testid="supplier-floating-cta"
          aria-label="Start free"
        >
          <Sparkles className="w-4 h-4" />
          <span>Start free</span>
          <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
        </button>
      </div>
    </div>
  );
};

export default SupplierLandingPage;
